import React, { useEffect, useState } from 'react';
import { useForm } from '../../Context/FormContext';
import { useAuth } from '../../Context/AuthContext';
import apiClient from '../../Services/AuthService';
import ContentEditable from 'react-contenteditable';
import SimpleMDE from 'react-simplemde-editor';
import 'easymde/dist/easymde.min.css';
import QuestionSimpleEditor from '../../Components/Admin/QuestionSimpleEditor';
import TextSelectableEditor from '../../Components/Admin/TextSelectableEditor';
import TextSimpleEditor from '../../Components/Admin/TextSimpleEditor';
import QuestionMultipleEditor from '../../Components/Admin/QuestionMultipleEditor';
import QuestionImageEditor from '../../Components/Admin/QuestionImageEditor';
import { challengeTypes, getChallengeTypeById } from '../../config/challengeTypes';
import { stripAllFormatting, htmlToPreviewText } from '../../utils/textUtils';

interface QuizGroup {
  groupId: string;
  groupName: string;
  groupSubtitle: string;
  created_at: string;
  position: number;
  sections: any[];
  generationPrompt?: string | null;
  refinementPrompt?: string | null;
}

interface Project {
  projectId?: string;
  projectName?: string;
  ownerName?: string;
  id?: string;
  internalName?: string;
  owner?: { id: string; username: string; firstName?: string };
  [key: string]: any;
}

interface QuizSection {
  sectionId: string;
  sectionName: string;
  recommendationText: string;
  sectionType: string;
  sectionTimeDuration: string;
  featured: boolean;
  isMandatory: boolean;
  summary: string;
  purpose: string;
  labels: string[];
  content: string;
  sectionContent: string; // JSON string containing type-specific data
  groupId: string;
  created_at: string;
}

interface SectionFormData {
  title: string;
  recommendationText: string;
  sectionType: string;
  timeDuration: string;
  hours: string;
  minutes: string;
  featured: boolean;
  is_mandatory: boolean;
  summary: string;
  purpose: string;
  clientType: string[];
  interventionType: string[];
  surfaceArea: string[];
}

// Question management interfaces
interface TextVariations {
  singular: string;
  plural: string;
}

interface Answer {
  id: string;
  text: string;
  isCorrect: boolean;
  isFreeText: boolean; // Whether this answer option allows free text input
  image?: string; // Optional image URL for the answer
}

interface SectionAnswer {
  id: string;
  text: string | TextVariations;
  isCorrect: boolean;
  isFreeText: boolean; // Whether this answer option allows free text input
  image?: string; // Optional image URL for the answer
}

interface QuestionConfig {
  allowMultiple?: boolean; // for multiple choice questions
  answers?: Answer[]; // for multiple choice questions
}

interface Question {
  id: string;
  text: string;
  type: 'free_response' | 'multiple_choice';
  position: number;
  config: QuestionConfig;
  image?: string; // Optional image URL for the question
}

type SectionContentType = 'TEXT_SIMPLE' | 'TEXT_SELECTABLE' | 'QUESTION_SIMPLE' | 'QUESTION_IMAGE' | 'QUESTION_MULTIPLE' | 'CHALLENGE' | 'IMAGE_COMMENT';

interface SectionContentItem {
  sectionContentId: string;
  sectionId: string;
  contentType: SectionContentType;
  sectionName: string;
  content: any;
  is_editable?: boolean;
  isEditable?: boolean; // Server returns this format
  // Legacy fields for backward compatibility
  id?: string;
  type?: SectionContentType;
  name?: string;
  data?: any;
}

interface SectionContentData {
  questions: Question[];
  items?: SectionContentItem[];
}

// Stable EasyMDE options (defined outside the component so the reference does
// not change on every render — otherwise the editor remounts on each keystroke).
const PROMPT_EDITOR_OPTIONS = {
  spellChecker: false,
  status: ['lines', 'words'] as ('lines' | 'words')[],
  // Height is driven entirely from CSS (.quiz-prompt-editor) so we can adapt to
  // the window viewport. Don't set minHeight/maxHeight here — those would emit
  // inline style="..." that overrides our CSS.
  toolbar: [
    'bold', 'italic', 'strikethrough', 'heading', '|',
    'quote', 'unordered-list', 'ordered-list', '|',
    'link', 'code', 'horizontal-rule', '|',
    'preview', 'side-by-side', 'fullscreen', '|',
    'guide',
  ] as any,
  placeholder: '',
  autofocus: false,
};

const QuizConfiguration: React.FC = () => {
  const { isSubmissionFormOpen, setIsSubmissionFormOpen } = useForm();
  const { user } = useAuth();
  const [groups, setGroups] = useState<QuizGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingGroup, setEditingGroup] = useState<QuizGroup | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [cascadeProjectsOnDelete, setCascadeProjectsOnDelete] = useState(false);
  const [activePromptTab, setActivePromptTab] = useState<'generation' | 'refinement'>('generation');
  const [isSectionFormOpen, setIsSectionFormOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<QuizSection | null>(null);
  const [editingSectionPosition, setEditingSectionPosition] = useState<number | null>(null);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [showDeleteSectionConfirm, setShowDeleteSectionConfirm] = useState(false);
  const [showDuplicateSectionConfirm, setShowDuplicateSectionConfirm] = useState(false);
  const [sectionToDuplicate, setSectionToDuplicate] = useState<QuizSection | null>(null);
  const [groupFormError, setGroupFormError] = useState<string>('');
  const [sectionFormError, setSectionFormError] = useState<string>('');
  const [draggedGroupIndex, setDraggedGroupIndex] = useState<number | null>(null);
  const [draggedSectionIndex, setDraggedSectionIndex] = useState<number | null>(null);
  const [draggedGroupId, setDraggedGroupId] = useState<string | null>(null);
  const [dragOverGroupIndex, setDragOverGroupIndex] = useState<number | null>(null);
  const [dragOverSectionIndex, setDragOverSectionIndex] = useState<number | null>(null);
  const [dragOverSectionGroupId, setDragOverSectionGroupId] = useState<string | null>(null);
  const [dragOverPosition, setDragOverPosition] = useState<'before' | 'after' | null>(null);
  const [dragOverFirstPosition, setDragOverFirstPosition] = useState<boolean>(false);
  const [dragOverLastPosition, setDragOverLastPosition] = useState<boolean>(false);
  const [dragOverFirstSectionGroup, setDragOverFirstSectionGroup] = useState<string | null>(null);
  const [dragOverLastSectionGroup, setDragOverLastSectionGroup] = useState<string | null>(null);
  const [isProcessingDrop, setIsProcessingDrop] = useState(false);
  const [lastProcessedDrop, setLastProcessedDrop] = useState<string | null>(null);
  const [isTextEditorOpen, setIsTextEditorOpen] = useState(false);
  const [editingTextSection, setEditingTextSection] = useState<QuizSection | null>(null);
  const [textEditorContent, setTextEditorContent] = useState<string>('');
  const [textEditorError, setTextEditorError] = useState<string>('');

  // Regenerate contents modal state
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateSuccess, setRegenerateSuccess] = useState(false);

  // Question management state
  const [isQuestionManagerOpen, setIsQuestionManagerOpen] = useState(false);
  const [editingQuestionSection, setEditingQuestionSection] = useState<QuizSection | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionManagerError, setQuestionManagerError] = useState<string>('');
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [isQuestionFormOpen, setIsQuestionFormOpen] = useState(false);
  const [questionFormData, setQuestionFormData] = useState({
    text: '',
    type: 'free_response' as 'free_response' | 'multiple_choice',
    allowMultiple: false,
    answers: [] as Answer[],
    questionImage: '' // Optional image for the question
  });
  
  // SectionContent management state
  const [isSectionContentManagerOpen, setIsSectionContentManagerOpen] = useState(false);
  const [editingSectionContentSection, setEditingSectionContentSection] = useState<QuizSection | null>(null);
  const [sectionContentItems, setSectionContentItems] = useState<SectionContentItem[]>([]);
  const [isLoadingSectionContent, setIsLoadingSectionContent] = useState(false);
  const [sectionContentManagerError, setSectionContentManagerError] = useState<string>('');
  const [sectionContentFormError, setSectionContentFormError] = useState<string>('');
  const [editingSectionContentItem, setEditingSectionContentItem] = useState<SectionContentItem | null>(null);
  const [isSectionContentFormOpen, setIsSectionContentFormOpen] = useState(false);
  const [sectionContentFormData, setSectionContentFormData] = useState<{
    name: string;
    type: SectionContentType;
    content: any; // Can be string or object with variations
    is_editable: boolean;
  }>({
    name: '',
    type: 'TEXT_SIMPLE' as SectionContentType,
    content: '',
    is_editable: false
  });
  const [showContentTypeSelection, setShowContentTypeSelection] = useState(false);
  
  // TEXT_SELECTABLE specific state
  const [textOptions, setTextOptions] = useState<string[]>([]);

  // CHALLENGE specific state
  const [selectedChallengeType, setSelectedChallengeType] = useState<string>('');
  
  // QUESTION_IMAGE specific state
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('');
  const [uploadedImageFilename, setUploadedImageFilename] = useState<string>('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  // QUESTION_MULTIPLE specific state
  const [questionAnswers, setQuestionAnswers] = useState<SectionAnswer[]>([]);
  const [allowMultipleAnswers, setAllowMultipleAnswers] = useState(false);
  
  // TEST content image states
  const [testQuestionImage, setTestQuestionImage] = useState<string>('');
  const [newAnswerImage, setNewAnswerImage] = useState<string>('');

  // SectionContent drag and drop state
  const [draggedContentIndex, setDraggedContentIndex] = useState<number | null>(null);
  const [dragOverContentIndex, setDragOverContentIndex] = useState<number | null>(null);

  // SectionContent delete confirmation state
  const [showDeleteContentConfirm, setShowDeleteContentConfirm] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<SectionContentItem | null>(null);
  
  const [formData, setFormData] = useState<{
    title: string;
    subtitle: string;
    generationPrompt: string;
    refinementPrompt: string;
  }>({
    title: '',
    subtitle: '',
    generationPrompt: '',
    refinementPrompt: '',
  });
  const [sectionFormData, setSectionFormData] = useState<SectionFormData>({
    title: '',
    recommendationText: '',
    sectionType: 'TEXT',
    timeDuration: '',
    hours: '0',
    minutes: '0',
    featured: false,
    is_mandatory: false,
    summary: '',
    purpose: '',
    clientType: [],
    interventionType: [],
    surfaceArea: []
  });


  const toggleForm = () => {
    setIsSubmissionFormOpen(!isSubmissionFormOpen);
    if (!isSubmissionFormOpen) {
      // Reset form when opening
      setFormData({
        title: '',
        subtitle: '',
        generationPrompt: '',
        refinementPrompt: '',
      });
      setEditingGroup(null);
      setGroupFormError('');
    } else {
      // Reset editing state when closing
      setEditingGroup(null);
      setGroupFormError('');
    }
  };

  const openEditForm = (group: QuizGroup) => {
    console.log('Opening edit form for group:', group);
    console.log('Group ID from parameter:', group.groupId);
    setEditingGroup(group);
    setFormData({
      title: group.groupName || '',
      subtitle: group.groupSubtitle || '',
      generationPrompt: group.generationPrompt ?? '',
      refinementPrompt: group.refinementPrompt ?? '',
    });
    setGroupFormError('');
    setIsSubmissionFormOpen(true);
  };

  const handleDeleteClick = () => {
    setCascadeProjectsOnDelete(false);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editingGroup) return;

    try {
      await apiClient.delete(`/quiz/group/${editingGroup.groupId}`, {
        params: { cascade_projects: cascadeProjectsOnDelete }
      });
      setShowDeleteConfirm(false);
      setCascadeProjectsOnDelete(false);
      toggleForm();
      fetchGroups();
    } catch (error: any) {
      console.error('Error deleting quiz group:', error);
      setShowDeleteConfirm(false);
      
      let errorMessage = 'Ha ocurrido un error al eliminar el grupo. Por favor, inténtalo de nuevo.';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.error === 'string') {
          errorMessage = data.error;
        } else if (typeof data.error === 'object' && data.error.message) {
          errorMessage = data.error.message;
        } else if (typeof data.message === 'string') {
          errorMessage = data.message;
        } else if (typeof data.message === 'object' && data.message.message) {
          errorMessage = data.message.message;
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (typeof data.detail === 'object' && data.detail.message) {
          errorMessage = data.detail.message;
        }
      }
      
      setGroupFormError(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setCascadeProjectsOnDelete(false);
  };

  // Section form handlers
  const toggleSectionForm = () => {
    setIsSectionFormOpen(!isSectionFormOpen);
    if (!isSectionFormOpen) {
      setSectionFormData({
        title: '',
        recommendationText: '',
        sectionType: 'TEXT',
        timeDuration: '',
        hours: '0',
        minutes: '0',
        featured: false,
        is_mandatory: false,
        summary: '',
        purpose: '',
        clientType: [],
        interventionType: [],
        surfaceArea: []
      });
      setEditingSection(null);
      setEditingSectionPosition(null);
      setCurrentGroupId(null);
      setSectionFormError('');
    } else {
      setEditingSection(null);
      setEditingSectionPosition(null);
      setCurrentGroupId(null);
      setSectionFormError('');
    }
  };

  const openCreateSectionForm = (groupId: string) => {
    setCurrentGroupId(groupId);
    setEditingSection(null);
    setEditingSectionPosition(null);
    setSectionFormData({
      title: '',
      recommendationText: '',
      sectionType: 'TEXT',
      timeDuration: '',
      hours: '0',
      minutes: '0',
      featured: false,
      is_mandatory: false,
      summary: '',
      purpose: '',
      clientType: [],
      interventionType: [],
      surfaceArea: []
    });
    setSectionFormError('');
    setIsSectionFormOpen(true);
  };

  const openEditSectionForm = (section: any, sectionIndex?: number) => {
    // Find the current group ID by looking for the section in the groups state
    let actualGroupId = section.groupId;
    for (const group of groups) {
      if (group.sections?.some((s: any) => s.sectionId === section.sectionId)) {
        actualGroupId = group.groupId;
        break;
      }
    }
    
    setCurrentGroupId(actualGroupId);
    setEditingSection(section);
    setEditingSectionPosition(sectionIndex ?? null);
    
    // Parse time duration back to hours and minutes
    let hours = '0';
    let minutes = '0';
    if (section.sectionTimeDuration) {
      const timeMatch = section.sectionTimeDuration.match(/(\d+)\s*hora?s?/);
      const minuteMatch = section.sectionTimeDuration.match(/(\d+)\s*minutos?/);
      if (timeMatch) hours = timeMatch[1];
      if (minuteMatch) minutes = minuteMatch[1];
    }
    
    // Parse labels to extract arrays of values
    let clientType: string[] = [];
    let interventionType: string[] = [];
    let surfaceArea: string[] = [];
    
    if (section.labels && Array.isArray(section.labels)) {
      section.labels.forEach((label: string) => {
        if (['PROFESSIONAL', 'FINAL_CUSTOMER'].includes(label)) {
          clientType.push(label);
        } else if (['NEW_CONSTRUCTION', 'RENOVATION'].includes(label)) {
          interventionType.push(label);
        } else if (['AREA_LOWER_THAN_50', 'AREA_BETWEEN_51_AND_100', 'AREA_BETWEEN_101_AND_200', 'AREA_BETWEEN_201_AND_500', 'AREA_GREATER_THAN_500'].includes(label)) {
          surfaceArea.push(label);
        }
      });
    }
    
    setSectionFormData({
      title: section.sectionName || '',
      recommendationText: section.recommendationText || '',
      sectionType: section.sectionType || 'TEXT',
      timeDuration: section.sectionTimeDuration || '',
      hours,
      minutes,
      featured: section.featured || false,
      is_mandatory: section.isMandatory || false,
      summary: section.sectionSummary || '',
      purpose: section.sectionPurpose || '',
      clientType,
      interventionType,
      surfaceArea
    });
    setSectionFormError('');
    setIsSectionFormOpen(true);
  };

  const handleSectionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSectionFormError(''); // Clear previous errors
    
    // Convert hours and minutes to a time string
    const hours = parseInt(sectionFormData.hours) || 0;
    const minutes = parseInt(sectionFormData.minutes) || 0;
    const timeString = hours > 0 ? 
      `${hours} hora${hours > 1 ? 's' : ''} ${minutes > 0 ? `${minutes} minutos` : ''}`.trim() :
      minutes > 0 ? `${minutes} minutos` : null;
    
    // Build labels array from selected values
    const labels: string[] = [];
    if (Array.isArray(sectionFormData.clientType)) {
      labels.push(...sectionFormData.clientType);
    }
    if (Array.isArray(sectionFormData.interventionType)) {
      labels.push(...sectionFormData.interventionType);
    }
    if (Array.isArray(sectionFormData.surfaceArea)) {
      labels.push(...sectionFormData.surfaceArea);
    }
    
    const payload = {
      name: sectionFormData.title,
      recommendation_text: sectionFormData.recommendationText || null,
      section_type: sectionFormData.sectionType,
      time_duration: timeString,
      group_id: currentGroupId,
      featured: sectionFormData.featured,
      is_mandatory: sectionFormData.is_mandatory,
      summary: sectionFormData.summary || null,
      purpose: sectionFormData.purpose || null,
      labels: labels,
      // Preserve position when editing existing section
      ...(editingSection && editingSectionPosition !== null && {
        position: editingSectionPosition
      })
    };
    
    try {
      if (editingSection) {
        // Update existing section
        console.log('Updating section with payload:', payload);
        await apiClient.put(`/quiz/section/${editingSection.sectionId}`, payload);
      } else {
        // Create new section
        console.log('Creating section with payload:', payload);
        await apiClient.post('/quiz/section', payload);
      }
      toggleSectionForm();
      fetchGroups(); // Refresh the list
    } catch (error: any) {
      console.error('Error saving quiz section:', error);
      console.error('Full error details:', error.response);
      
      // Set error message
      let errorMessage = 'Ha ocurrido un error al guardar la sección. Por favor, inténtalo de nuevo.';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.error === 'string') {
          errorMessage = data.error;
        } else if (typeof data.error === 'object' && data.error.message) {
          errorMessage = data.error.message;
        } else if (typeof data.message === 'string') {
          errorMessage = data.message;
        } else if (typeof data.message === 'object' && data.message.message) {
          errorMessage = data.message.message;
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (typeof data.detail === 'object' && data.detail.message) {
          errorMessage = data.detail.message;
        }
      }
      
      setSectionFormError(errorMessage);
    }
  };

  const handleSectionInputChange = (field: string, value: string | boolean) => {
    setSectionFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLabelToggle = (field: 'clientType' | 'interventionType' | 'surfaceArea', value: string) => {
    setSectionFormData(prev => {
      const currentArray = prev[field];
      const isSelected = currentArray.includes(value);
      
      return {
        ...prev,
        [field]: isSelected 
          ? currentArray.filter(item => item !== value)
          : [...currentArray, value]
      };
    });
  };

  const toggleSectionExpansion = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleDeleteSectionClick = () => {
    setShowDeleteSectionConfirm(true);
  };

  const handleDeleteSectionConfirm = async () => {
    if (!editingSection) return;
    
    try {
      await apiClient.delete(`/quiz/section/${editingSection.sectionId}`);
      setShowDeleteSectionConfirm(false);
      toggleSectionForm();
      fetchGroups();
    } catch (error) {
      console.error('Error deleting quiz section:', error);
    }
  };

  const handleDeleteSectionCancel = () => {
    setShowDeleteSectionConfirm(false);
  };

  // Duplicate section handlers
  const handleDuplicateSectionClick = (section: QuizSection) => {
    setSectionToDuplicate(section);
    setShowDuplicateSectionConfirm(true);
  };

  const handleDuplicateSectionConfirm = async () => {
    if (!sectionToDuplicate) return;
    
    try {
      await apiClient.post(`/quiz/section/${sectionToDuplicate.sectionId}/duplicate`);
      setShowDuplicateSectionConfirm(false);
      setSectionToDuplicate(null);
      fetchGroups();
    } catch (error: any) {
      console.error('Error duplicating quiz section:', error);
      
      // Log more detailed error information
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      }
      
      // Show user-friendly error message
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Error al duplicar la sección. Por favor, inténtalo de nuevo.';
      
      alert(errorMessage);
    }
  };

  const handleDuplicateSectionCancel = () => {
    setShowDuplicateSectionConfirm(false);
    setSectionToDuplicate(null);
  };

  // Text editor handlers
  const openTextEditor = async (section: QuizSection) => {
    setEditingTextSection(section);
    setTextEditorError('');
    setIsTextEditorOpen(true);
    
    try {
      const response = await apiClient.get(`/quiz/section/${section.sectionId}/content`);
      setTextEditorContent(response.data.content || '');
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Content doesn't exist yet, start with empty content
        setTextEditorContent('');
      } else {
        console.error('Error loading text content:', error);
        setTextEditorContent('');
      }
    }
  };

  const closeTextEditor = () => {
    setIsTextEditorOpen(false);
    setEditingTextSection(null);
    setTextEditorContent('');
    setTextEditorError('');
  };

  // Formatting functions for the editor
  const applyFormatting = (command: string, value: string = '') => {
    document.execCommand(command, false, value);
  };

  // Function to clean HTML formatting while preserving essential structure

  // Function to handle paste events - strip formatting
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const handleContentChange = (evt: any) => {
    setTextEditorContent(evt.target.value);
  };

  // JSON parsing/serialization functions for questions
  const parseSectionContent = (sectionContent: string): SectionContentData => {
    try {
      if (!sectionContent || sectionContent.trim() === '') {
        return { questions: [] };
      }
      const parsed = JSON.parse(sectionContent);
      return {
        questions: parsed.questions || []
      };
    } catch (error) {
      console.error('Error parsing section content:', error);
      return { questions: [] };
    }
  };

  const serializeSectionContent = (data: SectionContentData): string => {
    try {
      return JSON.stringify(data);
    } catch (error) {
      console.error('Error serializing section content:', error);
      return JSON.stringify({ questions: [] });
    }
  };

  const generateQuestionId = (): string => {
    return `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const generateAnswerId = (): string => {
    return `a_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Helper function to get label text
  const getLabelText = (label: string): string => {
    switch (label) {
      case 'PROFESSIONAL':
        return 'Arq';
      case 'FINAL_CUSTOMER':
        return 'Cliente';
      case 'NEW_CONSTRUCTION':
        return 'Obra Nueva';
      case 'RENOVATION':
        return 'Reforma';
      case 'AREA_LOWER_THAN_50':
        return '<50m²';
      case 'AREA_BETWEEN_51_AND_100':
        return '51-100m²';
      case 'AREA_BETWEEN_101_AND_200':
        return '101-200m²';
      case 'AREA_BETWEEN_201_AND_500':
        return '201-500m²';
      case 'AREA_GREATER_THAN_500':
        return '>500m²';
      default:
        return label;
    }
  };

  // Helper function to get content preview for section content items (first 150 characters)
  const getContentItemPreview = (item: SectionContentItem): string => {
    try {
      // Handle different content types
      const itemType = item.contentType || item.type;
      
      // htmlToPreviewText quita tags Y decodifica entidades HTML (&nbsp;, etc.),
      // evitando que la previsualización muestre entidades en crudo (bug #12).
      if (itemType === 'QUESTION_MULTIPLE') {
        // For multiple choice questions, show the "singular" text (new format) or "original" (legacy)
        if (item.content?.questionText && typeof item.content.questionText === 'object') {
          const textToShow = item.content.questionText.singular || item.content.questionText.original;
          if (textToShow) return htmlToPreviewText(textToShow, 150);
        } else if (typeof item.content?.questionText === 'string' && item.content.questionText) {
          return htmlToPreviewText(item.content.questionText, 150);
        } else if (item.content?.text) {
          return htmlToPreviewText(item.content.text, 150);
        }
        return '';
      } else if (itemType === 'QUESTION_SIMPLE') {
        // For simple questions, show the "singular" text if it exists (new format), fallback to "original" (legacy)
        if (item.content && typeof item.content === 'object') {
          const textToShow = item.content.singular || item.content.original;
          if (textToShow) return htmlToPreviewText(textToShow, 150);
        }
        return '';
      } else if (itemType === 'QUESTION_IMAGE') {
        // For image questions, show the "singular" text (new format) or "original" (legacy)
        if (item.content?.text && typeof item.content.text === 'object') {
          const textToShow = item.content.text.singular || item.content.text.original;
          if (textToShow) return htmlToPreviewText(textToShow, 150);
        } else if (typeof item.content?.text === 'string' && item.content.text) {
          return htmlToPreviewText(item.content.text, 150);
        }
        return '';
      } else if (itemType === 'TEXT_SELECTABLE') {
        // For text selectable, show the first option's "singular" text (new format) or "original" (legacy)
        const textOptions = item.content?.textOptions || item.data?.textOptions || [];
        if (textOptions.length > 0) {
          let optionText = '';

          // Check if it's new format (object with variations)
          if (typeof textOptions[0] === 'object') {
            optionText = textOptions[0].singular || textOptions[0].original || '';
          } else if (typeof textOptions[0] === 'string') {
            // Legacy format (string)
            optionText = textOptions[0];
          }

          if (optionText) return htmlToPreviewText(optionText, 150);
        }
        return '';
      } else if (itemType === 'TEXT_SIMPLE' || itemType === 'IMAGE_COMMENT') {
        // For TEXT_SIMPLE, show the "singular" text (new format) or "original" (legacy)
        if (item.content && typeof item.content === 'object' && item.content.singular) {
          return htmlToPreviewText(item.content.singular, 120);
        } else if (item.content && typeof item.content === 'object' && item.content.original) {
          return htmlToPreviewText(item.content.original, 120);
        } else if (item.content && typeof item.content === 'object' && item.content.text) {
          return htmlToPreviewText(item.content.text, 120);
        } else if (typeof item.content === 'string') {
          return htmlToPreviewText(item.content, 120);
        }
        return '';
      } else {
        // For other text content types (CHALLENGE, etc.)
        let textContent = '';

        if (typeof item.content === 'string') {
          textContent = item.content;
        } else if (item.content?.text) {
          textContent = item.content.text;
        } else if (item.content) {
          // Try to extract any text from the content object
          textContent = JSON.stringify(item.content);
        }

        if (textContent.length > 0) return htmlToPreviewText(textContent, 150);
      }

      return '';
    } catch (error) {
      console.error('Error getting content item preview:', error);
      return '';
    }
  };

  // Helper function to get content preview (first 100 characters)
  const getContentPreview = (section: QuizSection): string => {
    try {
      // htmlToPreviewText quita tags y decodifica entidades HTML (&nbsp;, etc.) (bug #12).
      // First try to get content from the content field
      if (section.content) {
        const preview = htmlToPreviewText(section.content, 100);
        if (preview.length > 0) {
          return preview;
        }
      }

      // If no content in main field, try to parse sectionContent JSON
      if (section.sectionContent) {
        try {
          const parsedContent = JSON.parse(section.sectionContent);

          // Handle different content types
          if (parsedContent.questions && parsedContent.questions.length > 0) {
            // For question content, show the first question text
            const firstQuestion = parsedContent.questions[0];
            const questionText = firstQuestion.text || firstQuestion.questionText || '';
            if (questionText.length > 0) {
              return htmlToPreviewText(questionText, 100);
            }
          }

          // For other content types, try to extract text
          if (typeof parsedContent === 'string') {
            return htmlToPreviewText(parsedContent, 100);
          }

          if (parsedContent.text) {
            return htmlToPreviewText(parsedContent.text, 100);
          }
        } catch (e) {
          // If JSON parsing fails, treat as plain text
          return htmlToPreviewText(section.sectionContent, 100);
        }
      }

      return '';
    } catch (error) {
      console.error('Error getting content preview:', error);
      return '';
    }
  };

  // Helper function to process and format labels for display
  const processLabelsForDisplay = (labels: string[]): { clientType: string[], interventionType: string[], surface: string } => {
    const clientType: string[] = [];
    const interventionType: string[] = [];
    const surfaceLabels: string[] = [];
    
    const allSurfaceLabels = [
      'AREA_LOWER_THAN_50',
      'AREA_BETWEEN_51_AND_100', 
      'AREA_BETWEEN_101_AND_200',
      'AREA_BETWEEN_201_AND_500',
      'AREA_GREATER_THAN_500'
    ];
    
    // Process each label in a specific order to ensure consistency
    labels.forEach(label => {
      if (['PROFESSIONAL', 'FINAL_CUSTOMER'].includes(label)) {
        const text = getLabelText(label);
        if (!clientType.includes(text)) {
          clientType.push(text);
        }
      } else if (['NEW_CONSTRUCTION', 'RENOVATION'].includes(label)) {
        const text = getLabelText(label);
        if (!interventionType.includes(text)) {
          interventionType.push(text);
        }
      } else if (allSurfaceLabels.includes(label)) {
        surfaceLabels.push(label);
      }
    });
    
    // Sort client type to ensure consistent order: Arq first, then Cliente
    clientType.sort((a, b) => {
      if (a === 'Arq') return -1;
      if (b === 'Arq') return 1;
      return 0;
    });
    
    // Sort intervention type to ensure consistent order: Obra Nueva first, then Reforma
    interventionType.sort((a, b) => {
      if (a === 'Obra Nueva') return -1;
      if (b === 'Obra Nueva') return 1;
      return 0;
    });
    
    // Sort surface labels by area size (smallest to largest)
    surfaceLabels.sort((a, b) => {
      const order = [
        'AREA_LOWER_THAN_50',
        'AREA_BETWEEN_51_AND_100',
        'AREA_BETWEEN_101_AND_200', 
        'AREA_BETWEEN_201_AND_500',
        'AREA_GREATER_THAN_500'
      ];
      return order.indexOf(a) - order.indexOf(b);
    });
    
    // Check if all surface labels are selected
    let surface = '';
    if (surfaceLabels.length === 5) {
      surface = 'Todas m²';
    } else if (surfaceLabels.length > 0) {
      surface = surfaceLabels.map(label => getLabelText(label)).join(', ');
    }
    
    return { clientType, interventionType, surface };
  };

  // Question management handlers
  const openQuestionManager = async (section: QuizSection) => {
    setEditingQuestionSection(section);
    setQuestionManagerError('');
    setIsQuestionManagerOpen(true);
    
    try {
      const response = await apiClient.get(`/quiz/section/${section.sectionId}/contents`);
      const contentData = parseSectionContent(response.data.content || '');
      setQuestions(contentData.questions);
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Content doesn't exist yet, start with empty questions
        setQuestions([]);
      } else {
        console.error('Error loading questions:', error);
        setQuestions([]);
      }
    }
  };

  const closeQuestionManager = () => {
    setIsQuestionManagerOpen(false);
    setEditingQuestionSection(null);
    setQuestions([]);
    setQuestionManagerError('');
    setEditingQuestion(null);
    setIsQuestionFormOpen(false);
  };

  const openQuestionForm = (question?: Question) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionFormData({
        text: question.text,
        type: question.type,
        allowMultiple: question.config.allowMultiple || false,
        answers: question.config.answers || [],
        questionImage: question.image || ''
      });
    } else {
      setEditingQuestion(null);
      setQuestionFormData({
        text: '',
        type: 'free_response',
        allowMultiple: false,
        answers: [],
        questionImage: ''
      });
    }
    setIsQuestionFormOpen(true);
  };

  const closeQuestionForm = () => {
    setIsQuestionFormOpen(false);
    setEditingQuestion(null);
    setQuestionFormData({
      text: '',
      type: 'free_response',
      allowMultiple: false,
      answers: [],
      questionImage: ''
    });
  };

  const handleQuestionFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!questionFormData.text.trim()) {
      setQuestionManagerError('El texto de la pregunta es requerido');
      return;
    }

    if (questionFormData.type === 'multiple_choice' && questionFormData.answers.length < 2) {
      setQuestionManagerError('Las preguntas de tipo test deben tener al menos 2 respuestas');
      return;
    }

    const question: Question = {
      id: editingQuestion?.id || generateQuestionId(),
      text: questionFormData.text,
      type: questionFormData.type,
      position: editingQuestion?.position || questions.length + 1,
      image: questionFormData.questionImage || undefined,
      config: {
        allowMultiple: questionFormData.type === 'multiple_choice' ? questionFormData.allowMultiple : undefined,
        answers: questionFormData.type === 'multiple_choice' ? questionFormData.answers : undefined
      }
    };

    let updatedQuestions;
    if (editingQuestion) {
      updatedQuestions = questions.map(q => q.id === editingQuestion.id ? question : q);
    } else {
      updatedQuestions = [...questions, question];
    }

    setQuestions(updatedQuestions);
    setQuestionManagerError('');
    closeQuestionForm();
  };

  const deleteQuestion = (questionId: string) => {
    const updatedQuestions = questions.filter(q => q.id !== questionId);
    // Reorder positions
    const reorderedQuestions = updatedQuestions.map((q, index) => ({
      ...q,
      position: index + 1
    }));
    setQuestions(reorderedQuestions);
  };

  const saveQuestions = async () => {
    if (!editingQuestionSection) return;

    try {
      const contentData: SectionContentData = { questions };
      const payload = {
        content: serializeSectionContent(contentData)
      };

      // Check if content already exists to determine if we need POST or PUT
      try {
        await apiClient.get(`/quiz/section/${editingQuestionSection.sectionId}/content`);
        // Content exists, use PUT
        await apiClient.put(`/quiz/section/${editingQuestionSection.sectionId}/content`, payload);
      } catch (getError: any) {
        if (getError.response?.status === 404) {
          // Content doesn't exist, use POST
          await apiClient.post(`/quiz/section/${editingQuestionSection.sectionId}/content`, payload);
        } else {
          throw getError;
        }
      }

      closeQuestionManager();
      fetchGroups();
    } catch (error: any) {
      console.error('Error saving questions:', error);
      
      let errorMessage = 'Ha ocurrido un error al guardar las preguntas. Por favor, inténtalo de nuevo.';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.error === 'string') {
          errorMessage = data.error;
        } else if (typeof data.error === 'object' && data.error.message) {
          errorMessage = data.error.message;
        } else if (typeof data.message === 'string') {
          errorMessage = data.message;
        }
      }
      
      setQuestionManagerError(errorMessage);
    }
  };

  // Multiple choice answer management
  const addAnswer = () => {
    const newAnswer: Answer = {
      id: generateAnswerId(),
      text: '',
      isCorrect: false,
      isFreeText: false
    };
    setQuestionFormData(prev => ({
      ...prev,
      answers: [...prev.answers, newAnswer]
    }));
  };

  const updateAnswer = (answerId: string, field: keyof Answer, value: string | boolean) => {
    setQuestionFormData(prev => ({
      ...prev,
      answers: prev.answers.map(answer => 
        answer.id === answerId ? { ...answer, [field]: value } : answer
      )
    }));
  };

  const removeAnswer = (answerId: string) => {
    setQuestionFormData(prev => ({
      ...prev,
      answers: prev.answers.filter(answer => answer.id !== answerId)
    }));
  };

  // Image handling functions
  const handleQuestionImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecciona un archivo de imagen válido.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo es demasiado grande. El tamaño máximo es 5MB.');
      return;
    }

    try {
      // For now, create a local URL for preview
      // In a real implementation, you would upload to your server
      const imageUrl = URL.createObjectURL(file);
      
      setQuestionFormData(prev => ({
        ...prev,
        questionImage: imageUrl
      }));
    } catch (error) {
      console.error('Error uploading question image:', error);
      alert('Error al subir la imagen. Por favor, inténtalo de nuevo.');
    }
  };

  const handleAnswerImageUpload = async (answerId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecciona un archivo de imagen válido.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo es demasiado grande. El tamaño máximo es 5MB.');
      return;
    }

    try {
      // For now, create a local URL for preview
      // In a real implementation, you would upload to your server
      const imageUrl = URL.createObjectURL(file);
      
      updateAnswer(answerId, 'image', imageUrl);
    } catch (error) {
      console.error('Error uploading answer image:', error);
      alert('Error al subir la imagen. Por favor, inténtalo de nuevo.');
    }
  };

  const removeQuestionImage = () => {
    setQuestionFormData(prev => ({
      ...prev,
      questionImage: ''
    }));
  };

  const removeAnswerImage = (answerId: string) => {
    updateAnswer(answerId, 'image', '');
  };

  // TEST content image handling functions
  const handleTestQuestionImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecciona un archivo de imagen válido.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo es demasiado grande. El tamaño máximo es 5MB.');
      return;
    }

    try {
      // For now, create a local URL for preview
      // In a real implementation, you would upload to your server
      const imageUrl = URL.createObjectURL(file);
      setTestQuestionImage(imageUrl);
    } catch (error) {
      console.error('Error uploading TEST question image:', error);
      alert('Error al subir la imagen. Por favor, inténtalo de nuevo.');
    }
  };

  const handleTestAnswerImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecciona un archivo de imagen válido.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo es demasiado grande. El tamaño máximo es 5MB.');
      return;
    }

    try {
      // For now, create a local URL for preview
      // In a real implementation, you would upload to your server
      const imageUrl = URL.createObjectURL(file);
      setNewAnswerImage(imageUrl);
    } catch (error) {
      console.error('Error uploading TEST answer image:', error);
      alert('Error al subir la imagen. Por favor, inténtalo de nuevo.');
    }
  };

  const removeTestQuestionImage = () => {
    setTestQuestionImage('');
  };

  const removeTestAnswerImage = () => {
    setNewAnswerImage('');
  };

  const handleQuestionTypeChange = (type: 'free_response' | 'multiple_choice') => {
    setQuestionFormData(prev => ({
      ...prev,
      type,
      // Initialize with 2 empty answers for multiple choice
      answers: type === 'multiple_choice' && prev.answers.length === 0 
        ? [
            { id: generateAnswerId(), text: '', isCorrect: false, isFreeText: false },
            { id: generateAnswerId(), text: '', isCorrect: false, isFreeText: false }
          ]
        : prev.answers
    }));
  };


  const handleTextEditorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTextEditorError('');

    if (!editingTextSection) return;

    try {
      const payload = {
        content: textEditorContent
      };

      // Check if content already exists to determine if we need POST or PUT
      try {
        await apiClient.get(`/quiz/section/${editingTextSection.sectionId}/content`);
        // Content exists, use PUT
        await apiClient.put(`/quiz/section/${editingTextSection.sectionId}/content`, payload);
      } catch (getError: any) {
        if (getError.response?.status === 404) {
          // Content doesn't exist, use POST
          await apiClient.post(`/quiz/section/${editingTextSection.sectionId}/content`, payload);
        } else {
          throw getError;
        }
      }

      closeTextEditor();
      fetchGroups();
    } catch (error: any) {
      console.error('Error saving text content:', error);
      
      let errorMessage = 'Ha ocurrido un error al guardar el contenido. Por favor, inténtalo de nuevo.';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.error === 'string') {
          errorMessage = data.error;
        } else if (typeof data.error === 'object' && data.error.message) {
          errorMessage = data.error.message;
        } else if (typeof data.message === 'string') {
          errorMessage = data.message;
        } else if (typeof data.message === 'object' && data.message.message) {
          errorMessage = data.message.message;
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (typeof data.detail === 'object' && data.detail.message) {
          errorMessage = data.detail.message;
        }
      }
      
      setTextEditorError(errorMessage);
    }
  };

  // Calculate total time duration for a group
  const calculateGroupTimeDuration = (sections: any[]) => {
    if (!sections || sections.length === 0) return null;
    
    let totalHours = 0;
    let totalMinutes = 0;
    
    sections.forEach((section: any) => {
      if (section.sectionTimeDuration) {
        const timeString = section.sectionTimeDuration;
        const hoursMatch = timeString.match(/(\d+)\s*hora?s?/);
        const minutesMatch = timeString.match(/(\d+)\s*minutos?/);
        
        if (hoursMatch) {
          totalHours += parseInt(hoursMatch[1]);
        }
        if (minutesMatch) {
          totalMinutes += parseInt(minutesMatch[1]);
        }
      }
    });
    
    // Convert excess minutes to hours
    totalHours += Math.floor(totalMinutes / 60);
    totalMinutes = totalMinutes % 60;
    
    // Format the result
    if (totalHours > 0 && totalMinutes > 0) {
      return `${totalHours} hora${totalHours > 1 ? 's' : ''} ${totalMinutes} minutos`;
    } else if (totalHours > 0) {
      return `${totalHours} hora${totalHours > 1 ? 's' : ''}`;
    } else if (totalMinutes > 0) {
      return `${totalMinutes} minutos`;
    }
    
    return null;
  };

  // SectionContent management functions
  const generateSectionContentId = (): string => {
    return `sc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const getContentTypeInfo = (type: SectionContentType) => {
    const typeInfo = {
      TEXT_SIMPLE: { 
        name: 'Texto Simple', 
        icon: 'bi-file-text', 
        tooltip: 'Texto en el centro de la pantalla' 
      },
      TEXT_SELECTABLE: { 
        name: 'Texto Seleccionable', 
        icon: 'bi-ui-checks', 
        tooltip: 'El arquitecto debe un texto entre múltiples opciones.' 
      },
      QUESTION_SIMPLE: { 
        name: 'Pregunta Abierta', 
        icon: 'bi-chat-text', 
        tooltip: 'El usuario debe responder con texto.' 
      },
      QUESTION_IMAGE: { 
        name: 'Pregunta con imagen', 
        icon: 'bi-image', 
        tooltip: 'El usuario debe responder con texto a una imagen propuesta.' 
      },
      QUESTION_MULTIPLE: { 
        name: 'Pregunta tipo test', 
        icon: 'bi-list-check', 
        tooltip: 'Pregunta tipo test' 
      },
      CHALLENGE: {
        name: 'Prueba',
        icon: 'bi-trophy',
        tooltip: 'Espacio reservado para la prueba'
      },
      IMAGE_COMMENT: {
        name: 'Subir imagen y comentar',
        icon: 'bi-upload',
        tooltip: 'El usuario debe subir una imagen y comentarla'
      }
    };
    return typeInfo[type];
  };

  const openSectionContentManager = async (section: QuizSection) => {
    setEditingSectionContentSection(section);
    setSectionContentManagerError('');
    setIsSectionContentManagerOpen(true);
    setIsLoadingSectionContent(true);
    
    try {
      const response = await apiClient.get(`/quiz/section/${section.sectionId}/contents`);
      console.log('Section content response:', response.data);
      
      // Handle different possible response formats
      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (response.data.items && Array.isArray(response.data.items)) {
        items = response.data.items;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        items = response.data.data;
      } else if (response.data) {
        // Single item response
        items = [response.data];
      }
      
      setSectionContentItems(items);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setSectionContentItems([]);
      } else {
        console.error('Error loading section content items:', error);
        setSectionContentItems([]);
      }
    } finally {
      setIsLoadingSectionContent(false);
    }
  };

  const closeSectionContentManager = () => {
    setIsSectionContentManagerOpen(false);
    setEditingSectionContentSection(null);
    setSectionContentItems([]);
    setSectionContentManagerError('');
    setEditingSectionContentItem(null);
    setIsSectionContentFormOpen(false);
    setShowContentTypeSelection(false);
  };

  // Helper function to normalize legacy content to new variation format
  const normalizeLegacyContent = (content: any, itemType: string): any => {
    if (!content) return '';

    // If already in new format with singular/plural
    if (typeof content === 'object' && content.singular !== undefined) {
      return {
        singular: content.singular || '',
        plural: content.plural || ''
      };
    }

    // If legacy format with original/tuSingular/etc, convert to new format
    if (typeof content === 'object' && content.original !== undefined) {
      return {
        singular: content.original || content.tuSingular || '',
        plural: content.tuPlural || ''
      };
    }
    
    // Extract text from legacy formats
    let textContent = '';
    if (typeof content === 'string') {
      textContent = content;
    } else if (typeof content === 'object') {
      if (content.text) {
        textContent = content.text;
      } else if (content.content && typeof content.content === 'string') {
        textContent = content.content;
      } else if (content.content && content.content.text) {
        textContent = content.content.text;
      } else if (content.questionText) {
        textContent = content.questionText;
      } else if (content.textContent) {
        textContent = content.textContent;
      } else if (content.value) {
        textContent = content.value;
      }
    }
    
    // For content types that use variations, normalize to variation format
    if ((itemType === 'TEXT_SIMPLE' || itemType === 'QUESTION_SIMPLE' || itemType === 'QUESTION_IMAGE') && textContent) {
      return {
        singular: textContent,
        plural: ''
      };
    }
    
    // For other types, return the extracted text or original content
    return textContent || content;
  };

  const openSectionContentForm = (item?: SectionContentItem, contentType?: SectionContentType) => {
    setSectionContentFormError(''); // Clear form errors
    if (item) {
      setEditingSectionContentItem(item);
      const formData = {
        name: item.sectionName || item.name || '',
        type: item.contentType || item.type || 'TEXT_SIMPLE',
        is_editable: item.isEditable !== undefined ? item.isEditable : (item.is_editable !== undefined ? item.is_editable : false),
        content: (() => {
          const itemType = item.contentType || item.type;
          let result: any = '';
          if (itemType === 'QUESTION_MULTIPLE') {
            // For QUESTION_MULTIPLE, preserve or normalize the structure
            if (item.content?.questionText && typeof item.content.questionText === 'object') {
              // Check if it's already normalized (has singular or original)
              if (item.content.questionText.singular !== undefined || item.content.questionText.original !== undefined) {
                result = normalizeLegacyContent(item.content.questionText, itemType);
              } else {
                result = normalizeLegacyContent(item.content.questionText, itemType);
              }
            } else if (item.content?.questionText) {
              // String format - normalize to variation format
              result = normalizeLegacyContent(item.content.questionText, itemType);
            } else {
              result = normalizeLegacyContent(item.content?.text || '', itemType);
            }
          } else if (itemType === 'QUESTION_SIMPLE') {
            // Normalize legacy content to variation format
            result = normalizeLegacyContent(item.content, itemType);
          } else if (itemType === 'TEXT_SELECTABLE') {
            // For TEXT_SELECTABLE, preserve the structure for the new editor
            if (item.content && typeof item.content === 'object' && item.content.textOptions) {
              result = item.content;
            } else {
              // Legacy format will be handled by textOptions state
              result = '';
            }
          } else if (itemType === 'TEXT_SIMPLE' || itemType === 'IMAGE_COMMENT') {
            // Normalize legacy content to variation format
            result = normalizeLegacyContent(item.content, itemType);
          } else if (itemType === 'QUESTION_IMAGE') {
            // For QUESTION_IMAGE, preserve or normalize the text structure
            if (item.content?.text && typeof item.content.text === 'object') {
              // Normalize to new format
              result = normalizeLegacyContent(item.content.text, itemType);
            } else if (item.content?.text) {
              // Legacy format - normalize to variation format
              result = normalizeLegacyContent(item.content.text, itemType);
            } else {
              result = normalizeLegacyContent('', itemType);
            }
          } else {
            result = normalizeLegacyContent(item.content, itemType);
          }
          return result;
        })()
      };
      setSectionContentFormData(formData);
      
      // Parse text options for TEXT_SELECTABLE
      const itemType = item.contentType || item.type;
      if (itemType === 'TEXT_SELECTABLE' && (item.content?.textOptions || item.data?.textOptions)) {
        const options = item.content?.textOptions || item.data?.textOptions || [];
        // Check if options are in new format (object with singular/plural or original) or legacy format (strings)
        if (options.length > 0 && typeof options[0] === 'object' && (options[0].singular !== undefined || options[0].original !== undefined)) {
          // New format - keep as is, will be handled by TextSelectableEditor
          setTextOptions([]);
        } else {
          // Legacy format - set textOptions for backward compatibility
          setTextOptions(options);
        }
      } else {
        setTextOptions([]);
      }
      
      // Parse answers for QUESTION_MULTIPLE
      if (itemType === 'QUESTION_MULTIPLE' && (item.content?.answers || item.data?.answers)) {
        setQuestionAnswers(item.content?.answers || item.data?.answers || []);
        setAllowMultipleAnswers(item.content?.allowMultiple || item.data?.allowMultiple || false);
        // Load TEST question image
        setTestQuestionImage(item.content?.questionImage || item.data?.questionImage || '');
      } else {
        setQuestionAnswers([]);
        setAllowMultipleAnswers(false);
        setTestQuestionImage('');
      }
      
      // Load image for QUESTION_IMAGE
      if (itemType === 'QUESTION_IMAGE' && item.content) {
        setUploadedImageUrl(item.content.imageUrl || '');
        setUploadedImageFilename(item.content.imageFilename || '');
      } else {
        setUploadedImageUrl('');
        setUploadedImageFilename('');
      }

      // Load challenge type for CHALLENGE
      if (itemType === 'CHALLENGE' && item.content?.challengeType) {
        setSelectedChallengeType(item.content.challengeType);
      } else {
        setSelectedChallengeType('');
      }
      
      setShowContentTypeSelection(false);
      setIsSectionContentFormOpen(true);
    } else if (contentType) {
      setEditingSectionContentItem(null);
      setSectionContentFormData({
        name: '',
        type: contentType,
        content: '',
        is_editable: false
      });
      setTextOptions([]);
      setQuestionAnswers([]);
      setAllowMultipleAnswers(false);
      setTestQuestionImage('');
      setNewAnswerImage('');
      setUploadedImageUrl('');
      setUploadedImageFilename('');
      setSelectedChallengeType(contentType === 'CHALLENGE' ? challengeTypes[0]?.id || '' : '');
      setShowContentTypeSelection(false);
      setIsSectionContentFormOpen(true);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setSectionContentFormError('Por favor selecciona una imagen válida (JPEG, JPG, PNG o WebP)');
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setSectionContentFormError('La imagen no debe superar los 10MB');
      return;
    }

    setIsUploadingImage(true);
    setSectionContentFormError('');

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await apiClient.post('/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setUploadedImageUrl(response.data.url);
        setUploadedImageFilename(response.data.filename);
      }
    } catch (error: any) {
      setSectionContentFormError(error.response?.data?.message || 'Error al subir la imagen');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageDelete = async () => {
    if (!uploadedImageFilename) return;

    try {
      await apiClient.delete(`/images/${uploadedImageFilename}`);
      setUploadedImageUrl('');
      setUploadedImageFilename('');
    } catch (error: any) {
      setSectionContentFormError(error.response?.data?.message || 'Error al eliminar la imagen');
    }
  };

  const showContentTypeSelector = () => {
    setShowContentTypeSelection(true);
  };

  // TEXT_SELECTABLE text options management

  // QUESTION_MULTIPLE answer management

  const closeSectionContentForm = () => {
    setIsSectionContentFormOpen(false);
    setEditingSectionContentItem(null);
    setSectionContentFormData({
      name: '',
      type: 'TEXT_SIMPLE',
      content: '',
      is_editable: false
    });
    setSectionContentFormError(''); // Clear form errors
    setTextOptions([]);
    setQuestionAnswers([]);
    setAllowMultipleAnswers(false);
    // Reset image states
    setUploadedImageUrl('');
    setUploadedImageFilename('');
    setIsUploadingImage(false);
  };

  const saveSectionContentItem = async () => {
    if (!editingSectionContentSection) return;
    
    try {
      let contentData: any = {};

      // Format content as JSON object based on type
      if (sectionContentFormData.type === 'TEXT_SELECTABLE') {
        // Check if content is already in new format with variations
        if (sectionContentFormData.content && typeof sectionContentFormData.content === 'object' && sectionContentFormData.content.textOptions) {
          contentData = sectionContentFormData.content;
        } else {
          // Legacy format - convert textOptions array to new format
          contentData = {
            textOptions: textOptions.map(text => ({
              singular: text,
              plural: ''
            }))
          };
        }
      } else if (sectionContentFormData.type === 'QUESTION_MULTIPLE') {
        // For QUESTION_MULTIPLE, check if content has variations or is legacy format
        if (typeof sectionContentFormData.content === 'object' && (sectionContentFormData.content.singular !== undefined || sectionContentFormData.content.original !== undefined)) {
          // Already has variations (new or legacy) - normalize it
          const normalized = normalizeLegacyContent(sectionContentFormData.content, 'QUESTION_MULTIPLE');
          contentData = {
            questionText: normalized,
            questionImage: testQuestionImage || undefined,
            answers: questionAnswers,
            allowMultiple: allowMultipleAnswers
          };
        } else {
          // String format - convert to new format
          contentData = {
            questionText: {
              singular: sectionContentFormData.content || '',
              plural: ''
            },
            questionImage: testQuestionImage || undefined,
            answers: questionAnswers,
            allowMultiple: allowMultipleAnswers
          };
        }
      } else if (sectionContentFormData.type === 'QUESTION_IMAGE') {
        // For QUESTION_IMAGE, check if content has variations or is legacy format
        if (typeof sectionContentFormData.content === 'object' && (sectionContentFormData.content.singular !== undefined || sectionContentFormData.content.original !== undefined)) {
          // Already has variations (new or legacy) - normalize it
          const normalized = normalizeLegacyContent(sectionContentFormData.content, 'QUESTION_IMAGE');
          contentData = {
            text: normalized,
            imageUrl: uploadedImageUrl,
            imageFilename: uploadedImageFilename
          };
        } else {
          // String format - convert to new format
          contentData = {
            text: {
              singular: sectionContentFormData.content || '',
              plural: ''
            },
            imageUrl: uploadedImageUrl,
            imageFilename: uploadedImageFilename
          };
        }
      } else if (sectionContentFormData.type === 'QUESTION_SIMPLE') {
        // For QUESTION_SIMPLE, prioritize new format (singular/plural)
        if (typeof sectionContentFormData.content === 'object' && sectionContentFormData.content.singular !== undefined) {
          // New format with singular/plural
          contentData = sectionContentFormData.content;
        } else if (typeof sectionContentFormData.content === 'object' && sectionContentFormData.content.original !== undefined) {
          // Legacy format with original - convert to new format
          contentData = {
            singular: sectionContentFormData.content.original || '',
            plural: sectionContentFormData.content.tuPlural || ''
          };
        } else {
          // Very old legacy format - convert to new format
          contentData = {
            singular: sectionContentFormData.content || '',
            plural: ''
          };
        }
      } else if (sectionContentFormData.type === 'TEXT_SIMPLE' || sectionContentFormData.type === 'IMAGE_COMMENT') {
        // For TEXT_SIMPLE, check format (new simplified format first, then legacy formats)
        if (typeof sectionContentFormData.content === 'object' &&
            'singular' in sectionContentFormData.content &&
            'plural' in sectionContentFormData.content) {
          // New simplified format with singular/plural
          contentData = {
            singular: sectionContentFormData.content.singular || '',
            plural: sectionContentFormData.content.plural || ''
          };
        } else if (typeof sectionContentFormData.content === 'object' && sectionContentFormData.content.original !== undefined) {
          // Legacy format with variations (original, tuSingular, etc.)
          contentData = sectionContentFormData.content;
        } else if (typeof sectionContentFormData.content === 'object' && sectionContentFormData.content.text !== undefined) {
          // Legacy format with text property - convert to new simplified format
          contentData = {
            singular: sectionContentFormData.content.text || '',
            plural: sectionContentFormData.content.text || ''
          };
        } else if (typeof sectionContentFormData.content === 'string') {
          // Legacy format - direct string - convert to new simplified format
          contentData = {
            singular: sectionContentFormData.content || '',
            plural: sectionContentFormData.content || ''
          };
        } else {
          // Default new simplified format
          contentData = {
            singular: '',
            plural: ''
          };
        }
      } else if (sectionContentFormData.type === 'CHALLENGE') {
        // For CHALLENGE, include challengeType and configuration
        try {
          const challengeConfig = typeof sectionContentFormData.content === 'string'
            ? JSON.parse(sectionContentFormData.content || '{}')
            : sectionContentFormData.content || {};

          contentData = {
            challengeType: selectedChallengeType,
            config: challengeConfig
          };
        } catch (error) {
          console.error('Error parsing challenge config:', error);
          contentData = {
            challengeType: selectedChallengeType,
            config: {}
          };
        }
      } else {
        // For other content types
        contentData = {
          text: sectionContentFormData.content
        };
      }

      let itemData: any = {
        sectionName: sectionContentFormData.name,
        content: contentData,
        contentType: sectionContentFormData.type,
        is_editable: sectionContentFormData.is_editable
      };

      let response;
      if (editingSectionContentItem) {
        // Update existing content
        response = await apiClient.put(`/quiz/section/${editingSectionContentSection.sectionId}/content/${editingSectionContentItem.sectionContentId || editingSectionContentItem.id}`, itemData);
      } else {
        // Create new content
        response = await apiClient.post(`/quiz/section/${editingSectionContentSection.sectionId}/content`, itemData);
      }

      // Refresh the list
      const listResponse = await apiClient.get(`/quiz/section/${editingSectionContentSection.sectionId}/contents`);
      console.log('Refresh section content response:', listResponse.data);
      
      // Handle different possible response formats
      let items = [];
      if (Array.isArray(listResponse.data)) {
        items = listResponse.data;
      } else if (listResponse.data.items && Array.isArray(listResponse.data.items)) {
        items = listResponse.data.items;
      } else if (listResponse.data.data && Array.isArray(listResponse.data.data)) {
        items = listResponse.data.data;
      } else if (listResponse.data) {
        // Single item response
        items = [listResponse.data];
      }
      
      setSectionContentItems(items);
      
      closeSectionContentForm();
    } catch (error: any) {
      console.error('Error saving section content item:', error);
      
      let errorMessage = 'Error al guardar el contenido de la sección';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.error === 'string') {
          errorMessage = data.error;
        } else if (typeof data.error === 'object' && data.error.message) {
          errorMessage = data.error.message;
        } else if (typeof data.message === 'string') {
          errorMessage = data.message;
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
      }
      
      setSectionContentFormError(errorMessage);
    }
  };

  const handleDeleteContentClick = (item: SectionContentItem) => {
    setContentToDelete(item);
    setShowDeleteContentConfirm(true);
  };

  const handleDeleteContentConfirm = async () => {
    if (!contentToDelete) return;
    
    const itemId = contentToDelete.sectionContentId || contentToDelete.id || '';
    await deleteSectionContentItem(itemId);
    
    setShowDeleteContentConfirm(false);
    setContentToDelete(null);
  };

  const handleDeleteContentCancel = () => {
    setShowDeleteContentConfirm(false);
    setContentToDelete(null);
  };

  const deleteSectionContentItem = async (itemId: string) => {
    if (!editingSectionContentSection) return;
    
    try {
      await apiClient.delete(`/quiz/section/${editingSectionContentSection.sectionId}/content/${itemId}`);

      // Refresh the list
      const listResponse = await apiClient.get(`/quiz/section/${editingSectionContentSection.sectionId}/contents`);
      console.log('Refresh section content response:', listResponse.data);
      
      // Handle different possible response formats
      let items = [];
      if (Array.isArray(listResponse.data)) {
        items = listResponse.data;
      } else if (listResponse.data.items && Array.isArray(listResponse.data.items)) {
        items = listResponse.data.items;
      } else if (listResponse.data.data && Array.isArray(listResponse.data.data)) {
        items = listResponse.data.data;
      } else if (listResponse.data) {
        // Single item response
        items = [listResponse.data];
      }
      
      setSectionContentItems(items);
    } catch (error: any) {
      console.error('Error deleting section content item:', error);
      setSectionContentManagerError('Error al eliminar el contenido de la sección');
    }
  };

  // SectionContent drag and drop handlers
  const handleContentDragStart = (e: React.DragEvent, contentIndex: number) => {
    console.log('handleContentDragStart called with index:', contentIndex);
    setDraggedContentIndex(contentIndex);
    e.dataTransfer.effectAllowed = 'move';
    (e.currentTarget as HTMLElement).style.opacity = '0.5';
  };

  const handleContentDragEnd = (e: React.DragEvent) => {
    setDraggedContentIndex(null);
    setDragOverContentIndex(null);
    (e.currentTarget as HTMLElement).style.opacity = '1';
  };

  const handleContentDragOver = (e: React.DragEvent, overIndex: number) => {
    e.preventDefault();
    if (draggedContentIndex !== null && draggedContentIndex !== overIndex) {
      setDragOverContentIndex(overIndex);
    }
  };

  const handleContentDragLeave = () => {
    setDragOverContentIndex(null);
  };

  const handleContentDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    console.log('handleContentDrop called with:', { draggedContentIndex, dropIndex });
    
    if (draggedContentIndex === null || draggedContentIndex === dropIndex) {
      console.log('Early return - same position or no dragged item');
      setDragOverContentIndex(null);
      return;
    }

    // Reorder items locally
    console.log('Current sectionContentItems:', sectionContentItems);
    const newItems = [...sectionContentItems];
    const draggedItem = newItems[draggedContentIndex];
    console.log('Dragged item:', draggedItem);
    newItems.splice(draggedContentIndex, 1);
    newItems.splice(dropIndex, 0, draggedItem);
    console.log('New items order:', newItems.map(item => ({ id: item.sectionContentId || item.id, name: item.sectionName || item.name })));
    
    setSectionContentItems(newItems);
    setDraggedContentIndex(null);
    setDragOverContentIndex(null);

    // Update positions in backend
    console.log('Starting backend update for positions...');
    try {
      const updatePromises = newItems.map((item, index) => {
        const payload = {
          content: item.content,
          position: index
        };
        console.log(`Updating item ${index}:`, {
          url: `/quiz/section/${item.sectionId}/content/${item.sectionContentId || item.id}`,
          payload
        });
        return apiClient.put(`/quiz/section/${item.sectionId}/content/${item.sectionContentId || item.id}`, payload);
      });
      
      await Promise.all(updatePromises);
      console.log('Content positions updated successfully');
    } catch (error) {
      console.error('Error updating content positions:', error);
      // Optionally show an error message to the user
      alert('Error al actualizar el orden del contenido. Por favor, recarga la página.');
    }
  };

  // Drag and drop handlers for groups
  const handleGroupDragStart = (e: React.DragEvent, groupIndex: number) => {
    setDraggedGroupIndex(groupIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleGroupDragEnd = () => {
    // Clear ALL drag states when drag ends (regardless of success)
    setTimeout(() => {
      setDraggedGroupIndex(null);
      setDragOverGroupIndex(null);
      setDragOverPosition(null);
      setDragOverFirstPosition(false);
      setDragOverLastPosition(false);
    }, 0);
  };

  const handleGroupDragOver = (e: React.DragEvent, overIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedGroupIndex !== null && draggedGroupIndex !== overIndex) {
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY;
      const midPoint = rect.top + (rect.height / 2);
      
      setDragOverGroupIndex(overIndex);
      setDragOverPosition(y < midPoint ? 'before' : 'after');
    }
  };

  const handleGroupDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the group entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverGroupIndex(null);
      setDragOverPosition(null);
    }
  };

  const handleGroupDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedGroupIndex === null) {
      setDraggedGroupIndex(null);
      setDragOverGroupIndex(null);
      setDragOverPosition(null);
      return;
    }

    const newGroups = [...groups];
    const draggedGroup = newGroups[draggedGroupIndex];
    
    // Calculate the actual drop position based on dragOverPosition
    let actualDropIndex = dropIndex;
    if (dragOverPosition === 'after') {
      actualDropIndex = dropIndex + 1;
    }
    
    // Adjust for the removal of the dragged item if it's before the drop position
    if (draggedGroupIndex < actualDropIndex) {
      actualDropIndex--;
    }
    
    // Prevent dropping on the same position
    if (draggedGroupIndex === actualDropIndex) {
      setDraggedGroupIndex(null);
      setDragOverGroupIndex(null);
      setDragOverPosition(null);
      return;
    }
    
    // Remove from old position and insert at new position
    newGroups.splice(draggedGroupIndex, 1);
    newGroups.splice(actualDropIndex, 0, draggedGroup);
    
    // Update local state immediately for smooth UX
    setGroups(newGroups);
    setDraggedGroupIndex(null);
    setDragOverGroupIndex(null);
    setDragOverPosition(null);

    // Update positions in backend
    try {
      const updatePromises = newGroups.map((group, index) => 
        apiClient.put(`/quiz/group/${group.groupId}`, {
          name: group.groupName,
          subtitle: group.groupSubtitle || null,
          position: index
        })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating group positions:', error);
      // Revert on error
      fetchGroups();
    }
  };

  // Drag and drop handlers for sections
  const handleSectionDragStart = (e: React.DragEvent, sectionIndex: number, groupId: string) => {
    setDraggedSectionIndex(sectionIndex);
    setDraggedGroupId(groupId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionDragEnd = () => {
    // Clear ALL drag states when drag ends (regardless of success)
    setTimeout(() => {
      setDraggedSectionIndex(null);
      setDraggedGroupId(null);
      setDragOverSectionIndex(null);
      setDragOverSectionGroupId(null);
      setDragOverPosition(null);
      setDragOverFirstSectionGroup(null);
      setDragOverLastSectionGroup(null);
      setIsProcessingDrop(false); // Reset the processing flag as well
      setLastProcessedDrop(null); // Reset the last processed drop ID
    }, 0);
  };

  // Helper function to create a unique drop ID
  const createDropId = (sectionIndex: number, groupId: string, targetIndex: number, targetGroupId: string) => {
    return `${sectionIndex}-${groupId}-${targetIndex}-${targetGroupId}-${Date.now()}`;
  };

  const handleSectionDragOver = (e: React.DragEvent, overIndex: number, groupId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Allow dropping in any group, not just the same group
    if (draggedSectionIndex !== null && (draggedGroupId !== groupId || draggedSectionIndex !== overIndex)) {
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY;
      const midPoint = rect.top + (rect.height / 2);
      const position = y < midPoint ? 'before' : 'after';
      
      setDragOverSectionIndex(overIndex);
      setDragOverSectionGroupId(groupId);
      setDragOverPosition(position);
    }
  };

  const handleSectionDragLeave = (e: React.DragEvent) => {
    // Disabled to prevent "bouncing" effect when dragging between sections
    // The dragEnd handler will clean up all states
  };

  const handleSectionDrop = async (e: React.DragEvent, dropIndex: number, groupId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear all drag states immediately
    const currentDraggedSectionIndex = draggedSectionIndex;
    const currentDraggedGroupId = draggedGroupId;
    
    // Prevent double processing of the same operation
    if (currentDraggedSectionIndex === null || currentDraggedGroupId === null) {
      return;
    }
    
    // Create a unique drop ID for this operation
    const dropId = createDropId(currentDraggedSectionIndex, currentDraggedGroupId, dropIndex, groupId);
    
    // Prevent duplicate processing
    if (isProcessingDrop || lastProcessedDrop === dropId) {
      return;
    }
    
    setIsProcessingDrop(true);
    setLastProcessedDrop(dropId);
    
    // Reset flag after operation
    setTimeout(() => {
      setIsProcessingDrop(false);
    }, 200);
    
    setDraggedSectionIndex(null);
    setDraggedGroupId(null);
    setDragOverSectionIndex(null);
    setDragOverSectionGroupId(null);
    setDragOverPosition(null);

    const newGroups = [...groups];
    const sourceGroupIndex = newGroups.findIndex(g => g.groupId === currentDraggedGroupId);
    const targetGroupIndex = newGroups.findIndex(g => g.groupId === groupId);
    
    if (sourceGroupIndex === -1 || targetGroupIndex === -1) {
      return;
    }

    // Get the dragged section
    const draggedSection = newGroups[sourceGroupIndex].sections[currentDraggedSectionIndex];
    
    // Commented out for debugging - this might be blocking valid moves
    // if (currentDraggedGroupId === groupId && currentDraggedSectionIndex === dropIndex) {
    //   return;
    // }
    
    // Calculate the actual drop position based on dragOverPosition
    let actualDropIndex = dropIndex;
    if (dragOverPosition === 'after') {
      actualDropIndex = dropIndex + 1;
    }
    
    // For same group moves, we need to account for the fact that removing an item
    // shifts the indices of items that come after it
    if (currentDraggedGroupId === groupId) {
      // If dragging from a position before the target, the target index shifts down by 1
      if (currentDraggedSectionIndex < actualDropIndex) {
        actualDropIndex--;
      }
      
      // Prevent dropping on the same position
      if (currentDraggedSectionIndex === actualDropIndex) {
        return;
      }
    }
    
    // Remove from source group
    newGroups[sourceGroupIndex].sections.splice(currentDraggedSectionIndex, 1);
    
    // Add to target group
    newGroups[targetGroupIndex].sections.splice(actualDropIndex, 0, draggedSection);
    
    // Update local state
    setGroups(newGroups);

    // Update positions in backend
    try {
      const updatePromises: Promise<any>[] = [];
      
      // Update all sections in the source group (if different from target)
      if (currentDraggedGroupId !== groupId) {
        newGroups[sourceGroupIndex].sections.forEach((section, index) => {
          updatePromises.push(
            apiClient.put(`/quiz/section/${section.sectionId}`, {
              name: section.sectionName,
              recommendation_text: section.recommendationText || null,
              section_type: section.sectionType,
              time_duration: section.sectionTimeDuration,
              group_id: currentDraggedGroupId,
              featured: section.featured,
              summary: section.sectionSummary || null,
              purpose: section.sectionPurpose || null,
              labels: section.labels || [],
              position: index
            })
          );
        });
      }
      
      // Update all sections in the target group (including the moved section with new group_id)
      newGroups[targetGroupIndex].sections.forEach((section, index) => {
        updatePromises.push(
          apiClient.put(`/quiz/section/${section.sectionId}`, {
            name: section.sectionName,
            recommendation_text: section.recommendationText || null,
            section_type: section.sectionType,
            time_duration: section.sectionTimeDuration,
            group_id: groupId, // Update group_id for the moved section
            featured: section.featured,
            summary: section.sectionSummary || null,
            purpose: section.sectionPurpose || null,
            labels: section.labels || [],
            position: index
          })
        );
      });
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating section positions:', error);
      // Revert on error
      fetchGroups();
    }
  };

  // Handlers for first/last position drops for groups
  const handleFirstGroupDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedGroupIndex !== null) {
      setDragOverFirstPosition(true);
    }
  };

  const handleFirstGroupDragLeave = () => {
    setDragOverFirstPosition(false);
  };

  const handleFirstGroupDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedGroupIndex === null || draggedGroupIndex === 0) {
      setDragOverFirstPosition(false);
      return;
    }

    const newGroups = [...groups];
    const draggedGroup = newGroups[draggedGroupIndex];
    
    newGroups.splice(draggedGroupIndex, 1);
    newGroups.unshift(draggedGroup);
    
    setGroups(newGroups);
    setDraggedGroupIndex(null);
    setDragOverFirstPosition(false);

    // Update positions in backend
    try {
      const updatePromises = newGroups.map((group, index) => 
        apiClient.put(`/quiz/group/${group.groupId}`, {
          name: group.groupName,
          subtitle: group.groupSubtitle || null,
          position: index
        })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating group positions:', error);
      fetchGroups();
    }
  };

  const handleLastGroupDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedGroupIndex !== null) {
      setDragOverLastPosition(true);
    }
  };

  const handleLastGroupDragLeave = () => {
    setDragOverLastPosition(false);
  };

  const handleLastGroupDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedGroupIndex === null || draggedGroupIndex === groups.length - 1) {
      setDragOverLastPosition(false);
      return;
    }

    const newGroups = [...groups];
    const draggedGroup = newGroups[draggedGroupIndex];
    
    newGroups.splice(draggedGroupIndex, 1);
    newGroups.push(draggedGroup);
    
    setGroups(newGroups);
    setDraggedGroupIndex(null);
    setDragOverLastPosition(false);

    // Update positions in backend
    try {
      const updatePromises = newGroups.map((group, index) => 
        apiClient.put(`/quiz/group/${group.groupId}`, {
          name: group.groupName,
          subtitle: group.groupSubtitle || null,
          position: index
        })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating group positions:', error);
      fetchGroups();
    }
  };

  // Handlers for first/last position drops for sections
  const handleFirstSectionDragOver = (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedSectionIndex !== null) {
      setDragOverFirstSectionGroup(groupId);
    }
  };

  const handleFirstSectionDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the zone entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverFirstSectionGroup(null);
    }
  };

  const handleFirstSectionDrop = async (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear all drag states immediately
    const currentDraggedSectionIndex = draggedSectionIndex;
    const currentDraggedGroupId = draggedGroupId;
    
    // Prevent double processing of the same operation
    if (currentDraggedSectionIndex === null || currentDraggedGroupId === null) {
      return;
    }
    
    // Create a unique drop ID for this operation (using 0 for first/last position)
    const dropId = createDropId(currentDraggedSectionIndex, currentDraggedGroupId, 0, groupId);
    
    // Prevent duplicate processing
    if (isProcessingDrop || lastProcessedDrop === dropId) {
      return;
    }
    
    setIsProcessingDrop(true);
    setLastProcessedDrop(dropId);
    
    // Reset flag after operation
    setTimeout(() => {
      setIsProcessingDrop(false);
    }, 200);
    
    setDraggedSectionIndex(null);
    setDraggedGroupId(null);
    setDragOverFirstSectionGroup(null);
    setDragOverSectionIndex(null);
    setDragOverSectionGroupId(null);
    setDragOverPosition(null);
    
    // If it's the same group and already at position 0, do nothing
    if (currentDraggedGroupId === groupId && currentDraggedSectionIndex === 0) {
      return;
    }

    const newGroups = [...groups];
    const sourceGroupIndex = newGroups.findIndex(g => g.groupId === currentDraggedGroupId);
    const targetGroupIndex = newGroups.findIndex(g => g.groupId === groupId);
    
    if (sourceGroupIndex === -1 || targetGroupIndex === -1) {
      return;
    }

    // Get the dragged section
    const draggedSection = newGroups[sourceGroupIndex].sections[currentDraggedSectionIndex];
    
    // Remove from source group
    newGroups[sourceGroupIndex].sections.splice(currentDraggedSectionIndex, 1);
    
    // Add to beginning of target group
    newGroups[targetGroupIndex].sections.unshift(draggedSection);
    
    setGroups(newGroups);

    // Update positions in backend
    try {
      const updatePromises: Promise<any>[] = [];
      
      // Update all sections in the source group (if different from target)
      if (currentDraggedGroupId !== groupId) {
        newGroups[sourceGroupIndex].sections.forEach((section, index) => {
          updatePromises.push(
            apiClient.put(`/quiz/section/${section.sectionId}`, {
              name: section.sectionName,
              recommendation_text: section.recommendationText || null,
              section_type: section.sectionType,
              time_duration: section.sectionTimeDuration,
              group_id: currentDraggedGroupId,
              featured: section.featured,
              summary: section.sectionSummary || null,
              purpose: section.sectionPurpose || null,
              labels: section.labels || [],
              position: index
            })
          );
        });
      }
      
      // Update all sections in the target group
      newGroups[targetGroupIndex].sections.forEach((section, index) => {
        updatePromises.push(
          apiClient.put(`/quiz/section/${section.sectionId}`, {
            name: section.sectionName,
            recommendation_text: section.recommendationText || null,
            section_type: section.sectionType,
            time_duration: section.sectionTimeDuration,
            group_id: groupId,
            featured: section.featured,
            summary: section.sectionSummary || null,
            purpose: section.sectionPurpose || null,
            labels: section.labels || [],
            position: index
          })
        );
      });
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating section positions:', error);
      fetchGroups();
    }
  };

  const handleLastSectionDragOver = (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedSectionIndex !== null) {
      setDragOverLastSectionGroup(groupId);
    }
  };

  const handleLastSectionDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the zone entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverLastSectionGroup(null);
    }
  };

  const handleLastSectionDrop = async (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear all drag states immediately
    const currentDraggedSectionIndex = draggedSectionIndex;
    const currentDraggedGroupId = draggedGroupId;
    
    // Prevent double processing of the same operation
    if (currentDraggedSectionIndex === null || currentDraggedGroupId === null) {
      return;
    }
    
    // Create a unique drop ID for this operation (using 0 for first/last position)
    const dropId = createDropId(currentDraggedSectionIndex, currentDraggedGroupId, 0, groupId);
    
    // Prevent duplicate processing
    if (isProcessingDrop || lastProcessedDrop === dropId) {
      return;
    }
    
    setIsProcessingDrop(true);
    setLastProcessedDrop(dropId);
    
    // Reset flag after operation
    setTimeout(() => {
      setIsProcessingDrop(false);
    }, 200);
    
    setDraggedSectionIndex(null);
    setDraggedGroupId(null);
    setDragOverLastSectionGroup(null);
    setDragOverSectionIndex(null);
    setDragOverSectionGroupId(null);
    setDragOverPosition(null);
    
    if (currentDraggedSectionIndex === null || currentDraggedGroupId === null) {
      return;
    }
    
    const sourceGroup = groups.find(g => g.groupId === currentDraggedGroupId);
    // If it's the same group and already at the last position, do nothing
    if (currentDraggedGroupId === groupId && sourceGroup && currentDraggedSectionIndex === sourceGroup.sections.length - 1) {
      return;
    }

    const newGroups = [...groups];
    const sourceGroupIndex = newGroups.findIndex(g => g.groupId === currentDraggedGroupId);
    const targetGroupIndex = newGroups.findIndex(g => g.groupId === groupId);
    
    if (sourceGroupIndex === -1 || targetGroupIndex === -1) {
      return;
    }

    // Get the dragged section
    const draggedSection = newGroups[sourceGroupIndex].sections[currentDraggedSectionIndex];
    
    // Remove from source group
    newGroups[sourceGroupIndex].sections.splice(currentDraggedSectionIndex, 1);
    
    // Add to end of target group
    newGroups[targetGroupIndex].sections.push(draggedSection);
    
    setGroups(newGroups);

    // Update positions in backend
    try {
      const updatePromises: Promise<any>[] = [];
      
      // Update all sections in the source group (if different from target)
      if (currentDraggedGroupId !== groupId) {
        newGroups[sourceGroupIndex].sections.forEach((section, index) => {
          updatePromises.push(
            apiClient.put(`/quiz/section/${section.sectionId}`, {
              name: section.sectionName,
              recommendation_text: section.recommendationText || null,
              section_type: section.sectionType,
              time_duration: section.sectionTimeDuration,
              group_id: currentDraggedGroupId,
              featured: section.featured,
              summary: section.sectionSummary || null,
              purpose: section.sectionPurpose || null,
              labels: section.labels || [],
              position: index
            })
          );
        });
      }
      
      // Update all sections in the target group
      newGroups[targetGroupIndex].sections.forEach((section, index) => {
        updatePromises.push(
          apiClient.put(`/quiz/section/${section.sectionId}`, {
            name: section.sectionName,
            recommendation_text: section.recommendationText || null,
            section_type: section.sectionType,
            time_duration: section.sectionTimeDuration,
            group_id: groupId,
            featured: section.featured,
            summary: section.sectionSummary || null,
            purpose: section.sectionPurpose || null,
            labels: section.labels || [],
            position: index
          })
        );
      });
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating section positions:', error);
      fetchGroups();
    }
  };

  // Fetch quiz groups
  const fetchGroups = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/quiz/groups');
      // Check if data is nested in another property
      if (response.data.data && Array.isArray(response.data.data)) {
        setGroups(response.data.data);
      } else if (response.data.groups && Array.isArray(response.data.groups)) {
        setGroups(response.data.groups);
      } else if (Array.isArray(response.data)) {
        setGroups(response.data);
      } else {
        console.error('Unexpected data structure:', response.data);
        setGroups([]);
      }
    } catch (error: any) {
      console.error('Error fetching quiz groups:', error);
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Regenerate contents functions
  const openRegenerateModal = async () => {
    setIsRegenerateModalOpen(true);
    try {
      const response = await apiClient.get('/quiz/admin/projects');
      const raw = response.data;
      const projectsData = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const closeRegenerateModal = () => {
    setIsRegenerateModalOpen(false);
    setSelectedProjectId('');
    setRegenerateSuccess(false);
  };

  const handleRegenerateContents = async () => {
    if (!selectedProjectId) return;

    setIsRegenerating(true);
    try {
      await apiClient.post(`/quiz/project/${selectedProjectId}/regenerate-contents`);
      setRegenerateSuccess(true);
    } catch (error) {
      console.error('Error regenerating contents:', error);
      alert('Error al regenerar contenidos');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGroupFormError(''); // Clear previous errors
    
    const payload = {
      name: formData.title,
      subtitle: formData.subtitle || null,
      generation_prompt: formData.generationPrompt?.trim() ? formData.generationPrompt : null,
      refinement_prompt: formData.refinementPrompt?.trim() ? formData.refinementPrompt : null,
    };
    
    try {
      if (editingGroup) {
        // Update existing group
        await apiClient.put(`/quiz/group/${editingGroup.groupId}`, payload);
      } else {
        // Create new group
        await apiClient.post('/quiz/group', payload);
      }
      toggleForm();
      fetchGroups(); // Refresh the list
    } catch (error: any) {
      console.error('Error saving quiz group:', error);
      console.error('Full error details:', error.response);
      
      // Set error message
      let errorMessage = 'Ha ocurrido un error al guardar el envío. Por favor, inténtalo de nuevo.';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.error === 'string') {
          errorMessage = data.error;
        } else if (typeof data.error === 'object' && data.error.message) {
          errorMessage = data.error.message;
        } else if (typeof data.message === 'string') {
          errorMessage = data.message;
        } else if (typeof data.message === 'object' && data.message.message) {
          errorMessage = data.message.message;
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (typeof data.detail === 'object' && data.detail.message) {
          errorMessage = data.detail.message;
        }
      }
      
      setGroupFormError(errorMessage);
    }
  };

  // Handle input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch groups on component mount
  useEffect(() => {
    fetchGroups();
  }, []);


  // Limpiar el estado cuando el componente se desmonte
  useEffect(() => {
    return () => {
      setIsSubmissionFormOpen(false);
    };
  }, [setIsSubmissionFormOpen]);

  return (
    <>
      <div className="container-custom py-4">
        <div className="row mb-4">
          <div className="col">
            <div className="d-flex justify-content-between align-items-center">
              <h1 className="h3 mb-0">Configurador del Cuestionario</h1>
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={openRegenerateModal}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Regenerar Contenidos
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center mt-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        ) : (
          <div className="row mt-4">
            <div className="col-12">
              {(!groups || !Array.isArray(groups) || groups.length === 0) ? (
                <p className="text-muted">No hay envíos creados aún</p>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {/* First position drop zone for groups */}
                  <div 
                    className={`drop-zone-first ${dragOverFirstPosition ? 'active' : ''}`}
                    onDragOver={handleFirstGroupDragOver}
                    onDragLeave={handleFirstGroupDragLeave}
                    onDrop={handleFirstGroupDrop}
                  >
                    {dragOverFirstPosition && (
                      <div className="drop-indicator">Soltar aquí para mover al principio</div>
                    )}
                  </div>
                  
                  {groups.map((group, index) => (
                    <div 
                      key={group.groupId || index} 
                      className={`quiz-group-card-full ${draggedGroupIndex === index ? 'dragging' : ''} ${
                        dragOverGroupIndex === index && draggedGroupIndex !== null && draggedGroupIndex !== index 
                          ? `drag-over drag-${dragOverPosition}` : ''
                      }`}
                      draggable
                      onDragStart={(e) => handleGroupDragStart(e, index)}
                      onDragEnd={handleGroupDragEnd}
                      onDragOver={(e) => handleGroupDragOver(e, index)}
                      onDragLeave={handleGroupDragLeave}
                      onDrop={(e) => handleGroupDrop(e, index)}
                    >
                      <div className="position-relative">
                        <div className="w-100">
                          <div className="d-flex justify-content-between align-items-start">
                            <h6 className="quiz-group-title">{group.groupName || 'Sin título'}</h6>
                            <div className="quiz-group-actions">
                              <button 
                                className="quiz-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditForm(group);
                                }}
                                title="Editar"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                            </div>
                          </div>
                          {(() => {
                            const calculatedTime = calculateGroupTimeDuration(group.sections);
                            if (group.groupSubtitle || calculatedTime) {
                              return (
                                <p className="quiz-group-subtitle">
                                  {group.groupSubtitle && calculatedTime 
                                    ? `${group.groupSubtitle}   |   Tiempo total aproximado ${calculatedTime}`
                                    : group.groupSubtitle 
                                    ? group.groupSubtitle
                                    : calculatedTime 
                                    ? `Tiempo total aproximado ${calculatedTime}`
                                    : ''
                                  }
                                </p>
                              );
                            }
                            return null;
                          })()}
                          
                          {/* Display existing sections */}
                          {(group.sections && group.sections.length > 0) || (!group.sections || group.sections.length === 0) ? (
                            <div className={`sections-container ${draggedSectionIndex !== null && draggedGroupId === group.groupId ? 'section-dragging' : ''}`}>
                              {/* First position drop zone for sections */}
                              <div 
                                className={`drop-zone-first-section ${dragOverFirstSectionGroup === group.groupId ? 'active' : ''}`}
                                onDragOver={(e) => handleFirstSectionDragOver(e, group.groupId)}
                                onDragLeave={handleFirstSectionDragLeave}
                                onDrop={(e) => handleFirstSectionDrop(e, group.groupId)}
                              >
                                {dragOverFirstSectionGroup === group.groupId && (
                                  <div className="drop-indicator">Soltar aquí para mover al principio</div>
                                )}
                              </div>
                              
                              {group.sections && group.sections.length > 0 ? (
                                group.sections.map((section: any, sectionIndex: number) => (
                                <div 
                                  key={section.sectionId} 
                                  className={`section-card mb-2 ${
                                    draggedSectionIndex === sectionIndex && draggedGroupId === group.groupId ? 'dragging' : ''
                                  } ${
                                    dragOverSectionIndex === sectionIndex && 
                                    dragOverSectionGroupId === group.groupId && 
                                    draggedSectionIndex !== null && 
                                    (draggedGroupId !== group.groupId || draggedSectionIndex !== sectionIndex) ? `drag-over drag-${dragOverPosition}` : ''
                                  }`}
                                  draggable
                                  onDragStart={(e) => handleSectionDragStart(e, sectionIndex, group.groupId)}
                                  onDragEnd={handleSectionDragEnd}
                                  onDragOver={(e) => handleSectionDragOver(e, sectionIndex, group.groupId)}
                                  onDragLeave={handleSectionDragLeave}
                                  onDrop={(e) => handleSectionDrop(e, sectionIndex, group.groupId)}
                                >
                                  <div 
                                    className="section-header d-flex justify-content-between align-items-center"
                                    onClick={() => toggleSectionExpansion(section.sectionId)}
                                  >
                                    <div className="d-flex align-items-center flex-wrap" style={{ flex: 1 }}>
                                      <div className="d-flex align-items-center">
                                        <i className={`bi ${
                                          section.sectionType === 'TEXT' ? 'bi-justify' :
                                          section.sectionType === 'QUESTION' ? 'bi-question-lg' :
                                          section.sectionType === 'CHALLENGE' ? 'bi-puzzle' :
                                          'bi-justify'
                                        } me-2`}></i>
                                        <h6 className="section-title mb-0">{section.sectionName}</h6>
                                        {section.featured && (
                                          <span 
                                            className="featured-icon ms-2"
                                            onMouseEnter={(e) => {
                                              const tooltip = document.createElement('div');
                                              tooltip.className = 'custom-tooltip';
                                              tooltip.textContent = 'Ejercicio destacado. Este ejercicio gusta mucho y da mucha info';
                                              document.body.appendChild(tooltip);
                                              
                                              const rect = e.currentTarget.getBoundingClientRect();
                                              const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                                              const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
                                              
                                              // Position tooltip above the icon, centered horizontally
                                              tooltip.style.left = rect.left + scrollLeft + (rect.width / 2) - (tooltip.offsetWidth / 2) + 'px';
                                              tooltip.style.top = rect.top + scrollTop - tooltip.offsetHeight - 8 + 'px';
                                              
                                              e.currentTarget.setAttribute('data-tooltip-id', Date.now().toString());
                                            }}
                                            onMouseLeave={() => {
                                              const tooltips = document.querySelectorAll('.custom-tooltip');
                                              tooltips.forEach(tooltip => tooltip.remove());
                                            }}
                                          >
                                            ☻
                                          </span>
                                        )}
                                      </div>
                                      {section.labels && section.labels.length > 0 && (() => {
                                        const { clientType, interventionType, surface } = processLabelsForDisplay(section.labels);
                                        const hasAnyLabels = clientType.length > 0 || interventionType.length > 0 || surface;
                                        
                                        if (!hasAnyLabels) {
                                          return null;
                                        }
                                        
                                        return (
                                          <div className="section-labels">
                                            {clientType.map((label, idx) => (
                                              <span key={`client-${idx}`} className="section-label">{label}</span>
                                            ))}
                                            {interventionType.map((label, idx) => (
                                              <span key={`intervention-${idx}`} className="section-label">{label}</span>
                                            ))}
                                            {surface && (
                                              <span className="section-label">{surface}</span>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    <div className="d-flex align-items-center gap-3">
                                      <span className="section-time text-muted" style={{ minWidth: '80px', textAlign: 'right' }}>
                                        {section.sectionTimeDuration || ''}
                                      </span>
                                      <i className={`bi ${expandedSections.includes(section.sectionId) ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                    </div>
                                  </div>
                                  
                                  {/* Show content preview only when section is not expanded */}
                                  {!expandedSections.includes(section.sectionId) && (() => {
                                    const preview = getContentPreview(section);
                                    return preview ? (
                                      <div className="section-preview">
                                        {preview}
                                      </div>
                                    ) : null;
                                  })()}
                                  
                                  {expandedSections.includes(section.sectionId) && (
                                    <div className="section-content mt-3 pt-3 border-top">
                                      <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div className="flex-grow-1">
                                          {section.recommendationText && (
                                            <div style={{ fontStyle: 'italic' }}>{section.recommendationText}</div>
                                          )}
                                        </div>
                                        <div className="d-flex align-items-center gap-1">
                                          <button 
                                            className="btn btn-sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openEditSectionForm(section, sectionIndex);
                                            }}
                                            title="Editar sección"
                                          >
                                            <i className="bi bi-pencil"></i>
                                          </button>
                                          <button 
                                            className="btn btn-sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDuplicateSectionClick(section);
                                            }}
                                            title="Duplicar sección"
                                          >
                                            <i className="bi bi-files"></i>
                                          </button>
                                          <button 
                                            className="btn btn-sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openSectionContentManager(section);
                                            }}
                                            title="Gestionar contenido de sección"
                                          >
                                            <i className="bi bi-gear"></i>
                                          </button>
                                        </div>
                                      </div>
                                      
                                      <div className="row mb-3">
                                        {section.sectionSummary && (
                                          <div className="col-md-6">
                                            <div className="mb-2">
                                              <strong>¿En qué consiste?</strong>
                                            </div>
                                            <div>{section.sectionSummary}</div>
                                          </div>
                                        )}
                                        {section.sectionPurpose && (
                                          <div className="col-md-6">
                                            <div className="mb-2">
                                              <strong>¿Para qué sirve?</strong>
                                            </div>
                                            <div>{section.sectionPurpose}</div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                ))
                              ) : (
                                /* Placeholder section for empty groups */
                                <div 
                                  key="placeholder-section"
                                  className={`section-card mb-2 ${
                                    dragOverSectionIndex === 0 && 
                                    dragOverSectionGroupId === group.groupId && 
                                    draggedSectionIndex !== null ? `drag-over drag-${dragOverPosition}` : ''
                                  }`}
                                  style={{ 
                                    minHeight: '60px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f8f9fa',
                                    border: '2px dashed #dee2e6',
                                    color: '#6c757d',
                                    fontStyle: 'italic',
                                    textAlign: 'center'
                                  }}
                                  onDragOver={(e) => handleSectionDragOver(e, 0, group.groupId)}
                                  onDragLeave={handleSectionDragLeave}
                                  onDrop={(e) => handleSectionDrop(e, 0, group.groupId)}
                                >
                                  No hay secciones, arrastra una sección existente o crea una nueva
                                </div>
                              )}
                              
                              {/* Last position drop zone for sections */}
                              <div 
                                className={`drop-zone-last-section ${dragOverLastSectionGroup === group.groupId ? 'active' : ''}`}
                                onDragOver={(e) => handleLastSectionDragOver(e, group.groupId)}
                                onDragLeave={handleLastSectionDragLeave}
                                onDrop={(e) => handleLastSectionDrop(e, group.groupId)}
                              >
                                {dragOverLastSectionGroup === group.groupId && (
                                  <div className="drop-indicator">Soltar aquí para mover al final</div>
                                )}
                              </div>
                            </div>
                          ) : null}
                          
                          <div className="mt-3">
                            <div 
                              className="create-section-box"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCreateSectionForm(group.groupId);
                              }}
                            >
                              +
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Last position drop zone for groups */}
                  <div 
                    className={`drop-zone-last ${dragOverLastPosition ? 'active' : ''}`}
                    onDragOver={handleLastGroupDragOver}
                    onDragLeave={handleLastGroupDragLeave}
                    onDrop={handleLastGroupDrop}
                  >
                    {dragOverLastPosition && (
                      <div className="drop-indicator">Soltar aquí para mover al final</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-12">
            <div 
              className="create-submission-box"
              onClick={toggleForm}
            >
              +&nbsp;&nbsp;&nbsp;Crear nuevo envío
            </div>
          </div>
        </div>
      </div>

      <div className={`submission-form-sidebar ${isSubmissionFormOpen ? 'open' : ''}`}>
        <button 
          className="close-form-btn"
          onClick={toggleForm}
          aria-label="Cerrar formulario"
        >
          ×
        </button>
        
        <div className="form-content d-flex flex-column h-100">
          <div className="flex-grow-1">
            <h4 className="mb-4">{editingGroup ? 'Editar Envío' : 'Nuevo Envío'}</h4>
            
            {groupFormError && (
              <div className="alert alert-danger" role="alert">
                {groupFormError}
              </div>
            )}
            
            <div className="form-fields">
              <div className="mb-3">
                <label className="form-label">Título del envío</label>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Ingrese el título"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
              </div>
              
              <div className="mb-3">
                <label className="form-label">Subtítulo</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Antes del anteproyecto"
                  value={formData.subtitle}
                  onChange={(e) => handleInputChange('subtitle', e.target.value)}
                />
              </div>

              <h6 className="mt-4 mb-3 text-uppercase text-muted" style={{ borderTop: '1px solid #dee2e6', paddingTop: '1rem' }}>
                Prompts IA
              </h6>

              <ul className="nav nav-tabs mb-0">
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${activePromptTab === 'generation' ? 'active' : ''}`}
                    onClick={() => setActivePromptTab('generation')}
                    style={{ color: '#000' }}
                  >
                    Generación
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${activePromptTab === 'refinement' ? 'active' : ''}`}
                    onClick={() => setActivePromptTab('refinement')}
                    style={{ color: '#000' }}
                  >
                    Refinamiento
                  </button>
                </li>
              </ul>

              {activePromptTab === 'generation' && (
                <div className="mb-3 quiz-prompt-editor">
                  <SimpleMDE
                    value={formData.generationPrompt}
                    onChange={(val) => handleInputChange('generationPrompt', val)}
                    options={PROMPT_EDITOR_OPTIONS}
                  />
                  <small className="text-muted d-block mt-2">
                    Si está vacío, se usará el prompt por defecto del sistema.
                  </small>
                </div>
              )}

              {activePromptTab === 'refinement' && (
                <div className="mb-3 quiz-prompt-editor">
                  <SimpleMDE
                    value={formData.refinementPrompt}
                    onChange={(val) => handleInputChange('refinementPrompt', val)}
                    options={PROMPT_EDITOR_OPTIONS}
                  />
                  <small className="text-muted d-block mt-2">
                    Si está vacío, no se aplicará refinamiento automático. Cambiar estos prompts no regenera informes ya creados.
                  </small>
                </div>
              )}
            </div>
          </div>

          <div className="border-top pt-3 mt-4">
            <form onSubmit={handleSubmit}>
              <div className="d-flex justify-content-between">
                <div>
                  {editingGroup && (
                    <button 
                      type="button" 
                      className="btn btn-delete-custom"
                      onClick={handleDeleteClick}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Eliminar
                    </button>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-cancel-custom" onClick={toggleForm}>Cancelar</button>
                  <button type="submit" className="btn btn-create-custom">
                    {editingGroup ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {isSubmissionFormOpen && <div className="form-overlay" onClick={toggleForm}></div>}

      {/* Section Form Sidebar */}
      <div className={`submission-form-sidebar ${isSectionFormOpen ? 'open' : ''}`}>
        <button 
          className="close-form-btn"
          onClick={toggleSectionForm}
          aria-label="Cerrar formulario"
        >
          ×
        </button>
        
        <div className="form-content d-flex flex-column h-100">
          <div className="flex-grow-1">
            <h4 className="mb-4">{editingSection ? 'Editar Sección' : 'Nueva Sección'}</h4>
            
            {sectionFormError && (
              <div className="alert alert-danger" role="alert">
                {sectionFormError}
              </div>
            )}
            
            <div className="form-fields">
            <div className="mb-3">
              <label className="form-label">Título de la sección</label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Ingrese el título"
                value={sectionFormData.title}
                onChange={(e) => handleSectionInputChange('title', e.target.value)}
                required
              />
            </div>
            
            <div className="mb-3">
              <label className="form-label">Recomendaciones</label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Ej: Antes del anteproyecto"
                value={sectionFormData.recommendationText}
                onChange={(e) => handleSectionInputChange('recommendationText', e.target.value)}
              />
            </div>
            
            <div className="mb-3">
              <label className="form-label">Tipo de sección</label>
              <select 
                className="form-select"
                value={sectionFormData.sectionType}
                onChange={(e) => handleSectionInputChange('sectionType', e.target.value)}
                required
              >
                <option value="TEXT">Texto</option>
                <option value="QUESTION">Pregunta</option>
                <option value="CHALLENGE">Ejercicio</option>
              </select>
            </div>
            
            <div className="mb-3">
              <label className="form-label">Tiempo aproximado</label>
              <div className="d-flex gap-2">
                <div className="flex-fill">
                  <label className="form-label small text-muted">Horas</label>
                  <select 
                    className="form-select"
                    value={sectionFormData.hours}
                    onChange={(e) => handleSectionInputChange('hours', e.target.value)}
                  >
                    {[...Array(4)].map((_, i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-fill">
                  <label className="form-label small text-muted">Minutos</label>
                  <select 
                    className="form-select"
                    value={sectionFormData.minutes}
                    onChange={(e) => handleSectionInputChange('minutes', e.target.value)}
                  >
                    {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(min => (
                      <option key={min} value={min}>{min}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="mb-3">
              <label className="form-label">¿En qué consiste?</label>
              <textarea 
                className="form-control" 
                rows={3}
                placeholder="Describe en qué consiste esta sección"
                value={sectionFormData.summary}
                onChange={(e) => handleSectionInputChange('summary', e.target.value)}
              />
            </div>
            
            <div className="mb-3">
              <label className="form-label">¿Para qué sirve?</label>
              <textarea 
                className="form-control" 
                rows={3}
                placeholder="Explica para qué sirve esta sección"
                value={sectionFormData.purpose}
                onChange={(e) => handleSectionInputChange('purpose', e.target.value)}
              />
            </div>
            
            <div className="mb-4">
              <label className="form-label">Etiquetas de clasificación</label>
              
              {/* Tipo de Cliente */}
              <div className="mb-3">
                <div className="small text-muted mb-2">Tipo de Cliente</div>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className={`btn btn-sm ${sectionFormData.clientType.includes('PROFESSIONAL') ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => handleLabelToggle('clientType', 'PROFESSIONAL')}
                  >
                    Arquitecto
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${sectionFormData.clientType.includes('FINAL_CUSTOMER') ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => handleLabelToggle('clientType', 'FINAL_CUSTOMER')}
                  >
                    Cliente Final
                  </button>
                </div>
              </div>
              
              {/* Tipo de Intervención */}
              <div className="mb-3">
                <div className="small text-muted mb-2">Tipo de Intervención</div>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className={`btn btn-sm ${sectionFormData.interventionType.includes('NEW_CONSTRUCTION') ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => handleLabelToggle('interventionType', 'NEW_CONSTRUCTION')}
                  >
                    Obra Nueva
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${sectionFormData.interventionType.includes('RENOVATION') ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => handleLabelToggle('interventionType', 'RENOVATION')}
                  >
                    Reforma
                  </button>
                </div>
              </div>
              
              {/* Superficie de Intervención */}
              <div className="mb-3">
                <div className="small text-muted mb-2">Superficie de Intervención</div>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className={`btn btn-sm ${sectionFormData.surfaceArea.includes('AREA_LOWER_THAN_50') ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => handleLabelToggle('surfaceArea', 'AREA_LOWER_THAN_50')}
                  >
                    &lt; 50m²
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${sectionFormData.surfaceArea.includes('AREA_BETWEEN_51_AND_100') ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => handleLabelToggle('surfaceArea', 'AREA_BETWEEN_51_AND_100')}
                  >
                    51 - 100m²
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${sectionFormData.surfaceArea.includes('AREA_BETWEEN_101_AND_200') ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => handleLabelToggle('surfaceArea', 'AREA_BETWEEN_101_AND_200')}
                  >
                    101 - 200m²
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${sectionFormData.surfaceArea.includes('AREA_BETWEEN_201_AND_500') ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => handleLabelToggle('surfaceArea', 'AREA_BETWEEN_201_AND_500')}
                  >
                    201 - 500m²
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${sectionFormData.surfaceArea.includes('AREA_GREATER_THAN_500') ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => handleLabelToggle('surfaceArea', 'AREA_GREATER_THAN_500')}
                  >
                    &gt; 500m²
                  </button>
                </div>
              </div>
            </div>
            
            <div className="mb-3">
              <div className="form-check form-switch">
                <input 
                  className="form-check-input" 
                  type="checkbox" 
                  role="switch"
                  id="featuredToggle"
                  checked={sectionFormData.featured}
                  onChange={(e) => handleSectionInputChange('featured', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="featuredToggle">
                  Marcar como Ejercicio Destacado
                </label>
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="mandatoryToggle"
                  checked={sectionFormData.is_mandatory}
                  onChange={(e) => handleSectionInputChange('is_mandatory', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="mandatoryToggle">
                  Marcar como Ejercicio Obligatorio
                </label>
              </div>
            </div>
            </div>
          </div>

          <div className="border-top pt-3 mt-4">
            <form onSubmit={handleSectionSubmit}>
              <div className="d-flex justify-content-between">
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-cancel-custom" onClick={toggleSectionForm}>Cancelar</button>
                  {editingSection && (
                    <button 
                      type="button" 
                      className="btn btn-delete-custom"
                      onClick={handleDeleteSectionClick}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Eliminar
                    </button>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-create-custom">
                    {editingSection ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {isSectionFormOpen && <div className="form-overlay" onClick={toggleSectionForm}></div>}

      {/* Text Editor Sidebar */}
      <div className={`submission-form-sidebar ${isTextEditorOpen ? 'open' : ''}`}>
        <button 
          className="close-form-btn"
          onClick={closeTextEditor}
          aria-label="Cerrar editor"
        >
          ×
        </button>
        
        <div className="form-content d-flex flex-column h-100">
          <div className="flex-grow-1">
            <h4 className="mb-4">Configurar Texto Libre</h4>
            
            {textEditorError && (
              <div className="alert alert-danger" role="alert">
                {textEditorError}
              </div>
            )}
            
            <div className="form-fields">
              {/* WYSIWYG Editor with ContentEditable */}
              <div className="mb-3">
                <div 
                  className="form-control"
                  style={{ 
                    minHeight: '600px', 
                    padding: '0',
                    overflow: 'hidden'
                  }}
                >
                  {/* Toolbar */}
                  <div className="d-flex justify-content-between align-items-center" style={{ 
                    padding: '8px 12px', 
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <div className="d-flex gap-1">
                      <button 
                        type="button" 
                        className="btn btn-sm"
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => applyFormatting('bold')}
                        title="Negrita"
                      >
                        <strong>B</strong>
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-sm"
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => applyFormatting('italic')}
                        title="Cursiva"
                      >
                        <em>I</em>
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-sm"
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => applyFormatting('underline')}
                        title="Subrayado"
                      >
                        <u>U</u>
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-sm"
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => {
                          // Try to clean selected text first, if no selection clean all
                          const selection = window.getSelection();
                          if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
                            // Clean format of selected text only
                            document.execCommand('removeFormat', false);
                          } else {
                            // Clean all formatting
                            setTextEditorContent(stripAllFormatting(textEditorContent));
                          }
                        }}
                        title="Limpiar formato (selección o todo)"
                      >
                        <i className="bi bi-eraser"></i>
                      </button>
                    </div>
                    <div className="d-flex gap-1">
                      <button 
                        type="button" 
                        className="btn btn-sm"
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          borderRadius: '4px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => applyFormatting('justifyLeft')}
                        title="Alinear izquierda"
                      >
                        <i className="bi bi-text-left"></i>
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-sm"
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          borderRadius: '4px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => applyFormatting('justifyCenter')}
                        title="Centrar"
                      >
                        <i className="bi bi-text-center"></i>
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-sm"
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          borderRadius: '4px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => applyFormatting('justifyRight')}
                        title="Alinear derecha"
                      >
                        <i className="bi bi-text-right"></i>
                      </button>
                    </div>
                    <div className="d-flex gap-1">
                      <button 
                        type="button" 
                        className="btn btn-sm"
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          borderRadius: '4px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => applyFormatting('insertUnorderedList')}
                        title="Lista desordenada"
                      >
                        <i className="bi bi-list-ul"></i>
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-sm"
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          padding: '4px 8px',
                          borderRadius: '4px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => applyFormatting('insertOrderedList')}
                        title="Lista ordenada"
                      >
                        <i className="bi bi-list-ol"></i>
                      </button>
                    </div>
                  </div>
                  
                  {/* ContentEditable Editor */}
                  <ContentEditable
                    html={textEditorContent}
                    disabled={false}
                    onChange={handleContentChange}
                    onPaste={handlePaste}
                    style={{ 
                      minHeight: '260px', 
                      padding: '12px',
                      lineHeight: '1.5',
                      border: 'none',
                      outline: 'none',
                      textAlign: 'left',
                      direction: 'ltr'
                    }}
                    placeholder="Escribe el contenido de texto aquí..."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-top pt-3 mt-4">
            <form onSubmit={handleTextEditorSubmit}>
              <div className="d-flex justify-content-between">
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-cancel-custom" onClick={closeTextEditor}>Cancelar</button>
                </div>
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-create-custom">Guardar</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {isTextEditorOpen && <div className="form-overlay" onClick={closeTextEditor}></div>}
      
      {/* Question Manager Sidebar */}
      <div className={`submission-form-sidebar ${isQuestionManagerOpen ? 'open' : ''}`}>
        <button 
          className="close-form-btn"
          onClick={closeQuestionManager}
          aria-label="Cerrar gestor de preguntas"
        >
          ×
        </button>
        
        <div className="form-content d-flex flex-column h-100">
          <div className="flex-grow-1">
            <h4 className="mb-4">Gestionar Preguntas</h4>
            
            {questionManagerError && (
              <div className="alert alert-danger" role="alert">
                {questionManagerError}
              </div>
            )}
            
            <div className="form-fields">
              {/* Questions List */}
              <div className="mb-3">
                <h6 className="mb-3">Preguntas ({questions.length})</h6>
                
                {questions.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-question-circle display-6 mb-2"></i>
                    <p>No hay preguntas configuradas</p>
                    <p className="small">Haz clic en "Nueva Pregunta" para comenzar</p>
                  </div>
                ) : (
                  <div className="question-list mb-3">
                    {questions.map((question, index) => (
                      <div 
                        key={question.id} 
                        className="question-item mb-3 p-3 border rounded"
                        style={{ 
                          borderColor: '#e0e0e0',
                          backgroundColor: '#fafafa',
                          cursor: 'grab'
                        }}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', question.id);
                          e.currentTarget.style.opacity = '0.5';
                        }}
                        onDragEnd={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.backgroundColor = '#f0f8ff';
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#fafafa';
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.backgroundColor = '#fafafa';
                          const draggedQuestionId = e.dataTransfer.getData('text/plain');
                          const draggedIndex = questions.findIndex(q => q.id === draggedQuestionId);
                          const targetIndex = index;
                          
                          if (draggedIndex !== targetIndex) {
                            const newQuestions = [...questions];
                            const [draggedQuestion] = newQuestions.splice(draggedIndex, 1);
                            newQuestions.splice(targetIndex, 0, draggedQuestion);
                            
                            // Update positions
                            const updatedQuestions = newQuestions.map((q, idx) => ({
                              ...q,
                              position: idx + 1
                            }));
                            
                            setQuestions(updatedQuestions);
                          }
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-start gap-2 flex-grow-1">
                            <i className="bi bi-grip-vertical" style={{ color: '#999', cursor: 'grab', marginTop: '2px' }}></i>
                            <div className="flex-grow-1">
                              <div className="question-text mb-2">{question.text}</div>
                              <div className="d-flex align-items-center gap-2">
                                <span className="small text-muted">
                                  {question.type === 'free_response' ? 'Respuesta Libre' : 'Test'}
                                </span>
                                {question.type === 'multiple_choice' && question.config.allowMultiple && (
                                  <span className="small text-muted">• Múltiples respuestas</span>
                                )}
                                {question.type === 'multiple_choice' && question.config.answers && (
                                  <span className="small text-muted">• {question.config.answers.length} opciones</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="d-flex gap-1">
                            <button 
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => openQuestionForm(question)}
                              title="Editar pregunta"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteQuestion(question.id)}
                              title="Eliminar pregunta"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <button 
                  type="button" 
                  className="btn btn-outline-dark w-100"
                  onClick={() => openQuestionForm()}
                >
                  <i className="bi bi-plus me-1"></i>
                  Nueva Pregunta
                </button>
              </div>
            </div>
          </div>

          <div className="border-top pt-3 mt-4">
            <div className="d-flex justify-content-between">
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-cancel-custom" onClick={closeQuestionManager}>Cancelar</button>
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-create-custom" onClick={saveQuestions}>Guardar Preguntas</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {isQuestionManagerOpen && <div className="form-overlay" onClick={closeQuestionManager}></div>}
      
      {/* Question Form Sidebar */}
      <div className={`submission-form-sidebar ${isQuestionFormOpen ? 'open' : ''}`}>
        <button 
          className="close-form-btn"
          onClick={closeQuestionForm}
          aria-label="Cerrar formulario de pregunta"
        >
          ×
        </button>
        
        <div className="form-content d-flex flex-column h-100">
          <div className="flex-grow-1">
            <h4 className="mb-4">{editingQuestion ? 'Editar Pregunta' : 'Nueva Pregunta'}</h4>
            
            <div className="form-fields">
              <form onSubmit={handleQuestionFormSubmit}>
                {/* Question Text */}
                <div className="mb-3">
                  <label className="form-label">Texto de la pregunta *</label>
                  <textarea 
                    className="form-control" 
                    rows={3}
                    placeholder="Escribe aquí el texto de la pregunta"
                    value={questionFormData.text}
                    onChange={(e) => setQuestionFormData(prev => ({ ...prev, text: e.target.value }))}
                    required
                  />
                </div>

                {/* Question Image */}
                <div className="mb-3">
                  <label className="form-label">Imagen de la pregunta (opcional)</label>
                  {!questionFormData.questionImage ? (
                    <div>
                      <input
                        type="file"
                        id="question-image-upload"
                        className="image-upload-input"
                        accept="image/*"
                        onChange={handleQuestionImageUpload}
                      />
                      <label htmlFor="question-image-upload" className="image-upload-button d-block">
                        <i className="bi bi-image me-2"></i>
                        Seleccionar imagen
                      </label>
                    </div>
                  ) : (
                    <div className="question-image-preview">
                      <img src={questionFormData.questionImage} alt="Vista previa de la pregunta" />
                      <button
                        type="button"
                        className="image-remove-btn"
                        onClick={removeQuestionImage}
                        title="Eliminar imagen"
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  )}
                </div>

                {/* Question Type */}
                <div className="mb-4">
                  <label className="form-label">Tipo de pregunta *</label>
                  <div className="d-flex gap-2">
                    <button 
                      type="button" 
                      className={`btn ${questionFormData.type === 'free_response' ? 'btn-dark' : 'btn-outline-dark'}`}
                      onClick={() => handleQuestionTypeChange('free_response')}
                    >
                      Respuesta Libre
                    </button>
                    <button 
                      type="button" 
                      className={`btn ${questionFormData.type === 'multiple_choice' ? 'btn-dark' : 'btn-outline-dark'}`}
                      onClick={() => handleQuestionTypeChange('multiple_choice')}
                    >
                      Múltiples respuestas (Tipo Test)
                    </button>
                  </div>
                </div>

                {/* Multiple Choice Configuration */}
                {questionFormData.type === 'multiple_choice' && (
                  <>
                    {/* Single vs Multiple Selection */}
                    <div className="mb-4">
                      <label className="form-label">Tipo de selección</label>
                      <div className="d-flex gap-2">
                        <button 
                          type="button" 
                          className={`btn ${!questionFormData.allowMultiple ? 'btn-dark' : 'btn-outline-dark'}`}
                          onClick={() => setQuestionFormData(prev => ({ ...prev, allowMultiple: false }))}
                        >
                          Una sola respuesta
                        </button>
                        <button 
                          type="button" 
                          className={`btn ${questionFormData.allowMultiple ? 'btn-dark' : 'btn-outline-dark'}`}
                          onClick={() => setQuestionFormData(prev => ({ ...prev, allowMultiple: true }))}
                        >
                          Múltiples respuestas
                        </button>
                      </div>
                    </div>

                    {/* Answer Options */}
                    <div className="mb-4">
                      <label className="form-label mb-3">Opciones de respuesta</label>
                      
                      {questionFormData.answers.map((answer, index) => (
                        <div 
                          key={answer.id} 
                          className="answer-item mb-3 p-3 border rounded"
                          style={{ 
                            borderColor: '#e0e0e0',
                            backgroundColor: '#fafafa',
                            cursor: 'grab'
                          }}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', answer.id);
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.style.backgroundColor = '#f0f8ff';
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fafafa';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.style.backgroundColor = '#fafafa';
                            const draggedAnswerId = e.dataTransfer.getData('text/plain');
                            const draggedIndex = questionFormData.answers.findIndex(a => a.id === draggedAnswerId);
                            const targetIndex = index;
                            
                            if (draggedIndex !== targetIndex) {
                              const newAnswers = [...questionFormData.answers];
                              const [draggedAnswer] = newAnswers.splice(draggedIndex, 1);
                              newAnswers.splice(targetIndex, 0, draggedAnswer);
                              
                              setQuestionFormData(prev => ({
                                ...prev,
                                answers: newAnswers
                              }));
                            }
                          }}
                        >
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <i className="bi bi-grip-vertical" style={{ color: '#999', cursor: 'grab' }}></i>
                            <input 
                              type="text" 
                              className="form-control"
                              placeholder="Texto de la respuesta"
                              value={answer.text}
                              onChange={(e) => updateAnswer(answer.id, 'text', e.target.value)}
                            />
                            {questionFormData.answers.length > 2 && (
                              <button 
                                type="button" 
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeAnswer(answer.id)}
                                title="Eliminar respuesta"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <div style={{ width: '24px' }}></div> {/* Spacer to align with grip icon */}
                            <div className="form-check">
                              <input 
                                className="form-check-input" 
                                type="checkbox"
                                id={`freetext_${answer.id}`}
                                checked={answer.isFreeText}
                                onChange={(e) => updateAnswer(answer.id, 'isFreeText', e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor={`freetext_${answer.id}`}>
                                Permitir que el usuario escriba su propia respuesta
                              </label>
                            </div>
                          </div>
                          
                          {/* Answer Image */}
                          <div className="d-flex align-items-start gap-2">
                            <div style={{ width: '24px' }}></div> {/* Spacer to align with grip icon */}
                            <div className="flex-grow-1">
                              <label className="form-label small">Imagen de la respuesta (opcional)</label>
                              {!answer.image ? (
                                <div>
                                  <input
                                    type="file"
                                    id={`answer-image-upload-${answer.id}`}
                                    className="image-upload-input"
                                    accept="image/*"
                                    onChange={(e) => handleAnswerImageUpload(answer.id, e)}
                                  />
                                  <label htmlFor={`answer-image-upload-${answer.id}`} className="image-upload-button d-block">
                                    <i className="bi bi-image me-2"></i>
                                    Seleccionar imagen
                                  </label>
                                </div>
                              ) : (
                                <div className="answer-image-preview">
                                  <img src={answer.image} alt="Vista previa de la respuesta" />
                                  <button
                                    type="button"
                                    className="image-remove-btn"
                                    onClick={() => removeAnswerImage(answer.id)}
                                    title="Eliminar imagen"
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      <button 
                        type="button" 
                        className="btn btn-outline-dark w-100 mb-3"
                        onClick={addAnswer}
                      >
                        <i className="bi bi-plus me-1"></i>
                        Añadir Opción
                      </button>
                      
                      {questionFormData.answers.length < 2 && (
                        <div className="alert alert-info">
                          <i className="bi bi-info-circle me-2"></i>
                          Las preguntas de tipo test deben tener al menos 2 opciones de respuesta.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>

          <div className="border-top pt-3 mt-4">
            <form onSubmit={handleQuestionFormSubmit}>
              <div className="d-flex justify-content-between">
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-cancel-custom" onClick={closeQuestionForm}>Cancelar</button>
                </div>
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-create-custom">
                    {editingQuestion ? 'Actualizar' : 'Crear'} Pregunta
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {isQuestionFormOpen && <div className="form-overlay" onClick={closeQuestionForm}></div>}
      
      {/* SectionContent Manager Sidebar */}
      <div className={`submission-form-sidebar section-content-form ${isSectionContentManagerOpen ? 'open' : ''}`}>
        <button 
          className="close-form-btn"
          onClick={closeSectionContentManager}
          aria-label="Cerrar gestor de contenido"
        >
          ×
        </button>
        
        <div className="form-content d-flex flex-column h-100">
          {showContentTypeSelection ? (
            // Content Type Selection Form
            <>
              <div className="flex-grow-1">
                <h4 className="mb-4">Selecciona el tipo de contenido a añadir</h4>
                <div className="row g-2">
                  {(['TEXT_SIMPLE', 'TEXT_SELECTABLE', 'QUESTION_SIMPLE', 'QUESTION_IMAGE', 'QUESTION_MULTIPLE', 'CHALLENGE', 'IMAGE_COMMENT'] as SectionContentType[]).map((contentType) => {
                    const typeInfo = getContentTypeInfo(contentType);
                    return (
                      <div key={contentType} className="col-6 col-md-4">
                        <button
                          type="button"
                          className="btn btn-outline-dark w-100 d-flex flex-column align-items-center p-3"
                          onClick={() => openSectionContentForm(undefined, contentType)}
                          title={typeInfo.tooltip}
                          style={{ 
                            minHeight: '80px',
                            transition: 'all 0.2s ease',
                            backgroundColor: 'white'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.7';
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <i className={`${typeInfo.icon} fs-4 mb-2 text-dark`}></i>
                          <small className="text-center text-dark">{typeInfo.name}</small>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="border-top pt-3 mt-4">
                <div className="d-flex justify-content-between">
                  <div className="d-flex gap-2">
                    <button 
                      type="button" 
                      className="btn btn-cancel-custom"
                      onClick={() => setShowContentTypeSelection(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Section Content List
            <>
              <div className="flex-grow-1">
                <h4 className="mb-4">
                  Contenido de Sección: {editingSectionContentSection?.sectionName}
                </h4>
                
                {sectionContentManagerError && (
                  <div className="alert alert-danger" role="alert">
                    {sectionContentManagerError}
                  </div>
                )}

                <div className="section-content-list">
                  {isLoadingSectionContent ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-dark mb-3" role="status">
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                      <div className="text-muted">Cargando contenido de la sección...</div>
                    </div>
                  ) : sectionContentItems.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                      <div className="mb-3">No hay contenido en esta sección</div>
                      <button 
                        type="button" 
                        className="btn btn-create-custom"
                        onClick={showContentTypeSelector}
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Añadir Contenido
                      </button>
                    </div>
                  ) : (
                    <>
                      {sectionContentItems.map((item, index) => (
                        <div 
                          key={item.sectionContentId || item.id} 
                          className={`section-content-item mb-3 p-3 border rounded ${
                            draggedContentIndex === index ? 'dragging' : ''
                          } ${
                            dragOverContentIndex === index ? 'drag-over' : ''
                          }`}
                          draggable
                          onDragStart={(e) => handleContentDragStart(e, index)}
                          onDragEnd={handleContentDragEnd}
                          onDragOver={(e) => handleContentDragOver(e, index)}
                          onDragLeave={handleContentDragLeave}
                          onDrop={(e) => handleContentDrop(e, index)}
                          style={{ cursor: 'grab' }}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-start gap-2 flex-grow-1">
                              <i className="bi bi-grip-vertical" style={{ color: '#999', cursor: 'grab', marginTop: '2px' }}></i>
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <i className={`${getContentTypeInfo(item.contentType || item.type || 'TEXT_SIMPLE').icon} text-dark`}></i>
                                  <h6 className="mb-0">{item.sectionName || item.name}</h6>
                                </div>
                                <small className="text-muted">
                                  {getContentTypeInfo(item.contentType || item.type || 'TEXT_SIMPLE').name}
                                </small>
                                {(() => {
                                  const preview = getContentItemPreview(item);
                                  return preview ? (
                                    <div className="content-item-preview">
                                      {preview}
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                              {(item.isEditable || item.is_editable) && (
                                <span className="badge bg-secondary me-2" title="Este contenido puede ser editado por arquitectos">
                                  Editable por el arquitecto
                                </span>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-dark"
                                onClick={() => openSectionContentForm(item)}
                                title="Editar"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-dark"
                                onClick={() => handleDeleteContentClick(item)}
                                title="Eliminar"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      <div className="text-center mt-3">
                        <button 
                          type="button" 
                          className="btn btn-create-custom"
                          onClick={showContentTypeSelector}
                        >
                          <i className="bi bi-plus-circle me-2"></i>
                          Añadir Contenido
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="border-top pt-3 mt-4">
                <div className="d-flex justify-content-between">
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-cancel-custom" onClick={closeSectionContentManager}>Cancelar</button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {isSectionContentManagerOpen && <div className="form-overlay" onClick={closeSectionContentManager}></div>}

      {/* SectionContent Form Sidebar */}
      <div className={`submission-form-sidebar section-content-form ${isSectionContentFormOpen ? 'open' : ''}`}>
        <button 
          className="close-form-btn"
          onClick={closeSectionContentForm}
          aria-label="Cerrar formulario de contenido"
        >
          ×
        </button>
        
        <div className="form-content d-flex flex-column h-100">
          <div className="flex-grow-1">
            <h4 className="mb-4">
              {editingSectionContentItem ? 'Editar Contenido' : 'Nuevo Contenido'}
            </h4>
            
            {sectionContentFormError && (
              <div className="alert alert-danger" role="alert">
                {sectionContentFormError}
              </div>
            )}
            
            <div className="mb-3">
              <label htmlFor="sectionContentType" className="form-label">
                Tipo de contenido *
              </label>
              <div className="d-flex align-items-center p-3 bg-light rounded">
                <i className={`${getContentTypeInfo(sectionContentFormData.type).icon} me-3 fs-4`}></i>
                <div>
                  <div className="fw-semibold">{getContentTypeInfo(sectionContentFormData.type).name}</div>
                  <small className="text-muted">{getContentTypeInfo(sectionContentFormData.type).tooltip}</small>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="sectionContentName" className="form-label">
                Nombre del contenido (sólo para uso interno) *
              </label>
              <input
                type="text"
                className="form-control"
                id="sectionContentName"
                value={sectionContentFormData.name}
                onChange={(e) => setSectionContentFormData(prev => ({
                  ...prev,
                  name: e.target.value
                }))}
                placeholder="Ej: Introducción, Ejercicios prácticos..."
              />
            </div>

            <div className="mb-3">
              {(sectionContentFormData.type === 'TEXT_SIMPLE' || sectionContentFormData.type === 'IMAGE_COMMENT') ? (
                <TextSimpleEditor
                  content={sectionContentFormData.content}
                  onChange={(newContent) => setSectionContentFormData(prev => ({
                    ...prev,
                    content: newContent
                  }))}
                  isEditable={sectionContentFormData.is_editable}
                  onEditableChange={(editable) => {
                    setSectionContentFormData(prev => ({
                      ...prev,
                      is_editable: editable
                    }));
                  }}
                />
              ) : sectionContentFormData.type === 'TEXT_SELECTABLE' ? (
                <TextSelectableEditor
                  content={textOptions.length > 0 ? textOptions : sectionContentFormData.content}
                  onChange={(newContent) => setSectionContentFormData(prev => ({
                    ...prev,
                    content: newContent
                  }))}
                  isEditable={sectionContentFormData.is_editable}
                  onEditableChange={(editable) => {
                    setSectionContentFormData(prev => ({
                      ...prev,
                      is_editable: editable
                    }));
                  }}
                />
              ) : sectionContentFormData.type === 'QUESTION_SIMPLE' ? (
                <QuestionSimpleEditor
                  content={sectionContentFormData.content}
                  onChange={(newContent) => setSectionContentFormData(prev => ({
                    ...prev,
                    content: newContent
                  }))}
                  isEditable={sectionContentFormData.is_editable}
                  onEditableChange={(editable) => {
                    setSectionContentFormData(prev => ({
                      ...prev,
                      is_editable: editable
                    }));
                  }}
                />
              ) : sectionContentFormData.type === 'QUESTION_MULTIPLE' ? (
                <QuestionMultipleEditor
                  content={{
                    questionText: sectionContentFormData.content,
                    questionImage: testQuestionImage,
                    answers: questionAnswers,
                    allowMultiple: allowMultipleAnswers
                  }}
                  onChange={(newContent) => {
                    setSectionContentFormData(prev => ({
                      ...prev,
                      content: newContent.questionText
                    }));
                    setQuestionAnswers(newContent.answers);
                  }}
                  testQuestionImage={testQuestionImage}
                  onQuestionImageChange={setTestQuestionImage}
                  allowMultipleAnswers={allowMultipleAnswers}
                  onAllowMultipleChange={setAllowMultipleAnswers}
                  isEditable={sectionContentFormData.is_editable}
                  onEditableChange={(editable) => {
                    setSectionContentFormData(prev => ({
                      ...prev,
                      is_editable: editable
                    }));
                  }}
                />
              ) : sectionContentFormData.type === 'QUESTION_IMAGE' ? (
                <QuestionImageEditor
                  content={{
                    text: sectionContentFormData.content,
                    imageUrl: uploadedImageUrl,
                    imageFilename: uploadedImageFilename
                  }}
                  onChange={(newContent) => {
                    setSectionContentFormData(prev => ({
                      ...prev,
                      content: newContent.text
                    }));
                  }}
                  uploadedImageUrl={uploadedImageUrl}
                  onImageUrlChange={setUploadedImageUrl}
                  uploadedImageFilename={uploadedImageFilename}
                  onImageFilenameChange={setUploadedImageFilename}
                  isUploadingImage={isUploadingImage}
                  onImageUpload={handleImageUpload}
                  onImageDelete={handleImageDelete}
                  isEditable={sectionContentFormData.is_editable}
                  onEditableChange={(editable) => {
                    setSectionContentFormData(prev => ({
                      ...prev,
                      is_editable: editable
                    }));
                  }}
                />
              ) : sectionContentFormData.type === 'CHALLENGE' ? (
                <div className="mb-3">
                  <label className="form-label" htmlFor="challengeTypeSelect">
                    Tipo de Challenge *
                  </label>
                  <select
                    className="form-select"
                    id="challengeTypeSelect"
                    value={selectedChallengeType}
                    onChange={(e) => setSelectedChallengeType(e.target.value)}
                    required
                  >
                    <option value="">Selecciona un tipo de challenge</option>
                    {challengeTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>

                  {/* Challenge specific content/configuration */}
                  <div className="mt-3">
                    <label className="form-label" htmlFor="challengeContentConfig">
                      Configuración del Challenge (JSON)
                    </label>
                    <textarea
                      className="form-control"
                      id="challengeContentConfig"
                      rows={6}
                      value={typeof sectionContentFormData.content === 'string' ? sectionContentFormData.content : JSON.stringify(sectionContentFormData.content, null, 2)}
                      onChange={(e) => setSectionContentFormData(prev => ({
                        ...prev,
                        content: e.target.value
                      }))}
                      placeholder="Configuración específica del challenge en formato JSON..."
                    />
                    <small className="text-muted">
                      Configuración específica para el tipo de challenge seleccionado
                    </small>
                  </div>
                </div>
              ) : (
                <textarea
                  className="form-control"
                  id="sectionContentContent"
                  rows={8}
                  value={sectionContentFormData.content}
                  onChange={(e) => setSectionContentFormData(prev => ({
                    ...prev,
                    content: e.target.value
                  }))}
                  placeholder="Ingresa el contenido..."
                />
              )}
            </div>
          </div>
          
          <div className="d-flex justify-content-between mt-3">
            <button 
              type="button" 
              className="btn btn-cancel-custom" 
              onClick={closeSectionContentForm}
              style={{ minWidth: '100px' }}
            >
              Cancelar
            </button>
            <button 
              type="button" 
              className="btn btn-dark" 
              style={{ 
                backgroundColor: '#000', 
                borderColor: '#000', 
                color: '#fff', 
                minWidth: '100px',
                opacity:
                  !sectionContentFormData.name.trim() ||
                  (sectionContentFormData.type === 'CHALLENGE' && !selectedChallengeType)
                    ? '0.6' : '1'
              }}
              onClick={saveSectionContentItem}
              disabled={
                !sectionContentFormData.name.trim() ||
                (sectionContentFormData.type === 'CHALLENGE' && !selectedChallengeType)
              }
            >
              {editingSectionContentItem ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </div>
      </div>

      {isSectionContentFormOpen && <div className="form-overlay" onClick={closeSectionContentForm}></div>}
      
      {/* Delete Confirmation Modal for Groups */}
      {showDeleteConfirm && (
        <>
          <div className="delete-confirm-modal">
            <div className="delete-confirm-content">
              <h5 className="mb-3">Confirmar eliminación</h5>
              <p className="mb-3">
                ¿Estás seguro de que quieres eliminar el envío "{editingGroup?.groupName}"?
                Esta acción no se puede deshacer.
              </p>
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="cascadeProjectsOnDelete"
                  checked={cascadeProjectsOnDelete}
                  onChange={(e) => setCascadeProjectsOnDelete(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="cascadeProjectsOnDelete">
                  Eliminar también de los proyectos existentes
                </label>
              </div>
              <p className="text-muted small mb-4">
                Si se desmarca, los proyectos existentes mantendrán una copia personalizada del grupo.
              </p>
              <div className="d-flex gap-2 justify-content-end">
                <button
                  className="btn btn-cancel-custom"
                  onClick={handleDeleteCancel}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-delete-custom"
                  onClick={handleDeleteConfirm}
                >
                  <i className="bi bi-trash me-1"></i>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
          <div className="delete-confirm-overlay" onClick={handleDeleteCancel}></div>
        </>
      )}
      
      {/* Delete Confirmation Modal for Sections */}
      {showDeleteSectionConfirm && (
        <>
          <div className="delete-confirm-modal">
            <div className="delete-confirm-content">
              <h5 className="mb-3">Confirmar eliminación</h5>
              <p className="mb-4">
                ¿Estás seguro de que quieres eliminar la sección "{editingSection?.sectionName}"? 
                Esta acción no se puede deshacer.
              </p>
              <div className="d-flex gap-2 justify-content-end">
                <button 
                  className="btn btn-cancel-custom"
                  onClick={handleDeleteSectionCancel}
                >
                  Cancelar
                </button>
                <button 
                  className="btn btn-delete-custom"
                  onClick={handleDeleteSectionConfirm}
                >
                  <i className="bi bi-trash me-1"></i>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
          <div className="delete-confirm-overlay" onClick={handleDeleteSectionCancel}></div>
        </>
      )}

      {/* Duplicate Confirmation Modal for Sections */}
      {showDuplicateSectionConfirm && (
        <>
          <div className="delete-confirm-modal">
            <div className="delete-confirm-content">
              <h5 className="mb-3">Confirmar duplicación</h5>
              <p className="mb-4">
                ¿Estás seguro de que quieres duplicar la sección "{sectionToDuplicate?.sectionName}"?
              </p>
              <div className="d-flex gap-2 justify-content-end">
                <button 
                  className="btn btn-cancel-custom"
                  onClick={handleDuplicateSectionCancel}
                >
                  Cancelar
                </button>
                <button 
                  className="btn btn-create-custom"
                  onClick={handleDuplicateSectionConfirm}
                >
                  <i className="bi bi-files me-1"></i>
                  Duplicar
                </button>
              </div>
            </div>
          </div>
          <div className="delete-confirm-overlay" onClick={handleDuplicateSectionCancel}></div>
        </>
      )}

      {/* Delete SectionContent Confirmation Modal */}
      {showDeleteContentConfirm && (
        <>
          <div className="delete-confirm-modal">
            <div className="delete-confirm-content">
              <h5>Confirmar eliminación</h5>
              <p>¿Estás seguro de que quieres eliminar el contenido "{contentToDelete?.sectionName || contentToDelete?.name}"?</p>
              <p className="text-muted small">Esta acción no se puede deshacer.</p>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button type="button" className="btn btn-cancel-custom" onClick={handleDeleteContentCancel}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-delete-custom" onClick={handleDeleteContentConfirm}>
                  <i className="bi bi-trash me-1"></i>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
          <div className="delete-confirm-overlay" onClick={handleDeleteContentCancel}></div>
        </>
      )}

      {/* Regenerate Contents Modal */}
      {isRegenerateModalOpen && (
        <div
          className="modal fade show d-block"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1050
          }}
          onClick={closeRegenerateModal}
        >
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Regenerar Contenidos</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeRegenerateModal}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="mb-4">
                    <p className="text-muted mb-3">
                      Selecciona el proyecto para el cual deseas regenerar los contenidos:
                    </p>

                    <select
                      className="form-select"
                      value={selectedProjectId}
                      onChange={(e) => setSelectedProjectId(e.target.value)}
                    >
                      <option value="">Seleccionar proyecto...</option>
                      {projects.map(project => (
                        <option key={project.projectId || project.id} value={project.projectId || project.id || ''}>
                          {project.internalName || project.projectName} - {project.owner?.firstName || project.ownerName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {regenerateSuccess ? (
                    <div className="alert alert-success" role="alert">
                      <i className="bi bi-check-circle me-2"></i>
                      ¡Contenidos regenerados exitosamente!
                    </div>
                  ) : (
                    <div className="alert alert-warning" role="alert">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Esta acción regenerará todos los contenidos del proyecto seleccionado. ¿Estás seguro?
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  {regenerateSuccess ? (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={closeRegenerateModal}
                    >
                      Cerrar
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeRegenerateModal}
                        disabled={isRegenerating}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleRegenerateContents}
                        disabled={!selectedProjectId || isRegenerating}
                      >
                        {isRegenerating ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Regenerando...
                          </>
                        ) : (
                          'Regenerar Contenidos'
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
      )}
    </>
  );
};

export default QuizConfiguration;